import React from "react";
import ModuleHeaderMenu from "../components/ModuleHeaderMenu";
import { buildNextRecordNumber, mapCostCenterCodes } from "../lib/recordNumberUtils";
import { matchesRecordNumberSearch } from "../lib/recordNumberSearch";
import { appendProvisionalSuffix, appendUpdatePendingDisplaySuffix, markOperationalCreatePending, markOperationalUpdatePending } from "../lib/operationalProvisional";
import { getAnimalCategoryAgeLabel, getAnimalCategoryOptions } from "../lib/animalCategoryRules";
import { buildSupplierDirectory } from "../lib/peopleRoleAdapters";
import { getNativeSafeAsset } from "../lib/platformAssets";

const hayBaleCardIcon = getNativeSafeAsset("/icons/shared/fardo-de-feno-card.webp");
const equineRationCardIcon = getNativeSafeAsset("/icons/equinos/saco-racao-equinos-card.webp");
const bovineRationCardIcon = getNativeSafeAsset("/icons/bovinos/saco-racao-bovinos-card.webp");
const genericRationCardIcon = getNativeSafeAsset("/icons/shared/saco-racao-generico-card.webp");
const cardImageStyle = { width: 34, height: 34, objectFit: "contain", display: "block" };
function renderCardImageIcon(src, alt){
  return <img src={src} alt={alt} loading="eager" decoding="async" fetchpriority="high" style={cardImageStyle} />;
}

export function createStockModule(deps){
  const {
    useState,
    useEffect,
    useRef,
    useCallback,
    useSysLists,
    useSync,
    useFarm,
    useFarmCollection,
    useGlobalCollection,
    useToast,
    useBgSyncToast,
    useSyncOnSave,
    makeSeed,
    canCreateInModule,
    canEditInModule,
    canEditCostCenter,
    getAllowedCostCentersForModule,
    itemMatchesCostCenterAccess,
    normalizePhotoEntries,
    compressImage,
    createClientId,
    today,
    fmtMoney,
    T,
    COST_CENTERS,
    STOCK_UNITS,
    STOCK_MOV_TYPES,
    parseNFe,
    mapUnit,
    setEditingFlag,
    Btn,
    Input,
    Field,
    Select,
    Textarea,
    Badge,
    Toast,
    ServicePhotoStatusIcon,
    ServicePhotoAddPhoneIcon,
    CCBadge,
    fmtDate,
    inputStyle,
    isCompactViewport,
    makeCreateMeta,
    makeUpdateMeta,
  } = deps;

  return function StockModule({ session, isGestor, onNavigate, initialTab="stock", showCatalogTab=true, hideTabs=false, moduleCardsConfig: moduleCardsConfigProp }) {
  const seed = makeSeed();
  const SL = useSysLists();

  const [moduleCardsConfigLocal, setModuleCardsConfigLocal] = useState(()=>{
    try{
      const parsed = JSON.parse(localStorage.getItem("fz:moduleCardsConfig") || "null");
      return parsed && typeof parsed === "object" ? parsed : {};
    }catch(_){ return {}; }
  });
  const [adminGlobalsConfigRows] = useGlobalCollection("adminGlobalsConfig", [{ id:"admin_globals_config", clientId:"admin_globals_config", moduleCardsConfig:{} }]);
  const syncCtx = useSync();
  const { fid: currentFarmId, farm } = useFarm();
  const [items,    setItems]      = useGlobalCollection("stock",          seed.stock);
  const [movements,setMovements]  = useFarmCollection("stockMovements", []);
  const [orders]                  = useFarmCollection("orders",         []);
  const [invoices, setInvoices]   = useFarmCollection("invoices",       []);
  const [animalLots]              = useFarmCollection("animalLots",     []);
  const [animals]                 = useFarmCollection("animals",        []);
  const [serviceNutritionEntries] = useFarmCollection("animalServiceNutrition", []);
  const [birdLotsNutrition]       = useFarmCollection("birdLotsNutrition", []);
  const [suppliers]               = useGlobalCollection("suppliers",     []);
  const [people]                  = useGlobalCollection("people",        []);
  const [tab,      setTab]        = useState(initialTab||"stock");
  const [form,     setForm]       = useState(null);
  const [movForm,  setMovForm]    = useState(null); // {itemId, type, qty, notes, costCenter}
  const [toast,    show]          = useToast();
  const syncOnSave = useSyncOnSave();
  const [stockLightbox, setStockLightbox] = useState(null);
  const [stockSearchOpen, setStockSearchOpen] = useState(false);
  const [stockSearchText, setStockSearchText] = useState("");
  const [stockFilterOpen, setStockFilterOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [stockFilters, setStockFilters] = useState({
    category: "",
    brand: "",
    supplier: "",
    costCenter: "",
    unit: "",
    stockState: "",
  });
  const stockNormalizeRef = useRef("");
  const costCenterCodeMap = React.useMemo(()=>mapCostCenterCodes(SL?.COST_CENTER_CODES || []), [SL]);
  const getStockPhotos = useCallback((item)=>normalizePhotoEntries((item?.photos||[]).length===0 && item?.photo ? [item.photo] : (item?.photos||[])),[]);
  const stockHasPhotos = useCallback((item)=>getStockPhotos(item).length>0,[getStockPhotos]);

  useEffect(()=>{
    function reloadModuleCardsConfig(){
      try{
        const parsed = JSON.parse(localStorage.getItem("fz:moduleCardsConfig") || "null");
        setModuleCardsConfigLocal(parsed && typeof parsed === "object" ? parsed : {});
      }catch(_){ setModuleCardsConfigLocal({}); }
    }
    reloadModuleCardsConfig();
    window.addEventListener("fz:modulecards:updated", reloadModuleCardsConfig);
    window.addEventListener("storage", reloadModuleCardsConfig);
    window.addEventListener("focus", reloadModuleCardsConfig);
    return ()=>{
      window.removeEventListener("fz:modulecards:updated", reloadModuleCardsConfig);
      window.removeEventListener("storage", reloadModuleCardsConfig);
      window.removeEventListener("focus", reloadModuleCardsConfig);
    };
  }, []);

  const syncedModuleCardsConfig = React.useMemo(()=>{
    const row = Array.isArray(adminGlobalsConfigRows) ? adminGlobalsConfigRows[0] : null;
    return row?.moduleCardsConfig && typeof row.moduleCardsConfig === "object" ? row.moduleCardsConfig : {};
  }, [adminGlobalsConfigRows]);

  const moduleCardsConfig = React.useMemo(()=>{
    const globalConfig = syncedModuleCardsConfig && typeof syncedModuleCardsConfig === "object" ? syncedModuleCardsConfig : {};
    const propConfig = moduleCardsConfigProp && typeof moduleCardsConfigProp === "object" ? moduleCardsConfigProp : {};
    const localConfig = moduleCardsConfigLocal && typeof moduleCardsConfigLocal === "object" ? moduleCardsConfigLocal : {};
    return {
      ...globalConfig,
      ...propConfig,
      ...localConfig,
      stock: {
        ...(globalConfig?.stock && typeof globalConfig.stock === "object" ? globalConfig.stock : {}),
        ...(propConfig?.stock && typeof propConfig.stock === "object" ? propConfig.stock : {}),
        ...(localConfig?.stock && typeof localConfig.stock === "object" ? localConfig.stock : {}),
      },
    };
  }, [syncedModuleCardsConfig, moduleCardsConfigProp, moduleCardsConfigLocal]);

  const getConfiguredSectionCards = React.useCallback((sectionId, fallback=[])=>{
    const moduleConfig = moduleCardsConfig?.stock;
    const aliases = {
      "moduleHome": ["moduleHome"],
      "menu:estoque": ["menu:estoque", "menu:stock"],
    };
    const keys = aliases[sectionId] || [sectionId];
    for(const key of keys){
      if(moduleConfig && Object.prototype.hasOwnProperty.call(moduleConfig, key)){
        const configured = moduleConfig?.[key];
        if(Array.isArray(configured)) return configured.map(v=>String(v||"")).filter(Boolean).slice(0,4);
        return [];
      }
    }
    return (Array.isArray(fallback) ? fallback : []).map(v=>String(v||"")).filter(Boolean).slice(0,4);
  }, [moduleCardsConfig]);


  // NF-e import state
  const [nfeData,    setNfeData]    = useState(null);
  const [nfePreview, setNfePreview] = useState(null);
  const [nfeLoading, setNfeLoading] = useState(false);
  const [editInvoice,setEditInvoice]= useState(null);
  const xmlInputRef = useRef();


  const dedupeBy = (list, keyFn) => {
    const map = new Map();
    (list || []).forEach((entry, idx) => {
      if (!entry) return;
      const key = keyFn(entry, idx);
      if (!key) return;
      if (!map.has(key)) map.set(key, entry);
    });
    return Array.from(map.values());
  };

  const stockRecordActivityTs = (record) => {
    const value = record?.updatedAt || record?.updated_at || record?.modifiedAt || record?.createdAt || record?.created_at || record?.ts || "";
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const compareStockByLastActivity = React.useCallback((a, b) => {
    const diff = stockRecordActivityTs(b) - stockRecordActivityTs(a);
    if (diff !== 0) return diff;
    return String(a?.name || a?.recordNumber || a?.id || "").localeCompare(String(b?.name || b?.recordNumber || b?.id || ""), "pt-BR");
  }, []);

  function makeProvisionalStockRecordNumber(record = {}) {
    const base = record?.recordNumber ? { recordNumber: record.recordNumber } : buildNextRecordNumber(items.filter(Boolean), { moduleKey:"stock", costCenter:record?.costCenter, costCenterCodeMap });
    return { ...base, recordNumber: appendProvisionalSuffix(base.recordNumber || record?.recordNumber || "") };
  }

  function displayStockRecordNumber(record) {
    if (!record) return "—";
    const rn = String(record.recordNumber || record.id || "").trim();
    if (!rn) return "—";
    if (record.pendingLocalUpdate && !record.pendingLocalCreate) return appendUpdatePendingDisplaySuffix(rn);
    return rn;
  }

  const markStockCreatePending = React.useCallback((record, at)=> markOperationalCreatePending(record, { userName: session?.name || "", at }), [session?.name]);
  const markStockUpdatePending = React.useCallback((record, at)=> markOperationalUpdatePending(record, { userName: session?.name || "", at }), [session?.name]);

  const makeStockMovementEntry = React.useCallback((payload = {}, at = null) => {
    const ts = at || payload.ts || payload.updatedAt || new Date().toISOString();
    const id = payload.clientId || payload.id || createClientId("smv");
    return markOperationalCreatePending({
      ...payload,
      id,
      clientId: id,
      farmId: payload.farmId || currentFarmId || "",
      qty: Number(payload.qty || 0),
      ts,
      updatedAt: ts,
      createdAt: payload.createdAt || ts,
      createdBy: payload.createdBy || payload.by || session?.name || "",
    }, { userName: session?.name || payload.by || "", at: ts, operationMeta:{ operationKind:"stockMovement" } });
  }, [currentFarmId, session?.name]);

  const normalizeStockItem = useCallback((item) => {
    if (!item) return item;
    const actor = session?.name || item?.updatedBy || item?.createdBy || "";
    const stableId = item.id || item.clientId || createClientId("stk");
    return {
      ...item,
      id: stableId,
      clientId: item.clientId || item.id || stableId,
      farmId: item.farmId || currentFarmId || "",
      minQty: Number(item.minQty || 0),
      unitCost: Number(item.unitCost || 0),
      pkgQty: item.pkgQty === "" || item.pkgQty == null ? "" : Number(item.pkgQty),
      pkgUnitRef: item.pkgUnitRef || "",
      baleWeightKg: item.baleWeightKg === "" || item.baleWeightKg == null ? "" : Number(item.baleWeightKg),
      forageBase: item.forageBase || "",
      hayQuality: item.hayQuality || "Boa",
      hayGuaranteeLevels: item.hayGuaranteeLevels || item.guaranteeLevels || "",
      targetSpecies: Array.isArray(item.targetSpecies) ? item.targetSpecies.map(v=>String(v||"").trim()).filter(Boolean) : [],
      guaranteeItems: (()=>{
        if(Array.isArray(item.guaranteeItems) && item.guaranteeItems.length){
          return item.guaranteeItems.map(entry=>({
            id: entry?.id || createClientId("gar"),
            nutrient: entry?.nutrient || "",
            amount: entry?.amount ?? "",
            unit: entry?.unit || "g/kg",
            notes: entry?.notes || "",
          }));
        }
        if(item.guaranteeLevels){
          return parseGuaranteeText(item.guaranteeLevels);
        }
        return [];
      })(),
      notes: item.notes || "",
      brand: item.brand || "",
      supplier: item.supplier || "",
      supplierId: item.supplierId || "",
      photos: normalizePhotoEntries((item?.photos || []).length===0 && item?.photo ? [item.photo] : (item?.photos || [])),
      ...(!item.createdAt ? makeCreateMeta(actor, {}) : {}),
    };
  }, [currentFarmId, session?.name]);

  const normalizeMovement = useCallback((mov) => {
    if (!mov) return mov;
    const actor = session?.name || mov?.updatedBy || mov?.createdBy || mov?.by || "";
    const stableId = mov.id || mov.clientId || createClientId("smv");
    return {
      ...mov,
      id: stableId,
      clientId: mov.clientId || mov.id || stableId,
      farmId: mov.farmId || currentFarmId || "",
      qty: Number(mov.qty || 0),
      notes: mov.notes || "",
      costCenter: mov.costCenter || COST_CENTERS[0],
      ...(!mov.createdAt ? makeCreateMeta(actor, { updatedAt: mov.updatedAt || mov.ts || new Date().toISOString() }) : {}),
    };
  }, [currentFarmId, session?.name]);

  const ensureStockRecordNumbers = useCallback((records)=>{
    const baseRecords = Array.isArray(records) ? records.slice() : [];
    const sorted = baseRecords.map((record, index)=>({ record, index, stamp: String(record?.createdAt || record?.updatedAt || index) }))
      .sort((a,b)=> String(a.stamp).localeCompare(String(b.stamp)));
    let changed = false;
    const working = baseRecords.slice();
    sorted.forEach(({ record, index })=>{
      if (!record || record.recordNumber) return;
      const next = buildNextRecordNumber(working.filter(Boolean), { moduleKey:"stock", costCenter:record?.costCenter, costCenterCodeMap });
      working[index] = { ...record, ...next };
      changed = true;
    });
    return changed ? working : baseRecords;
  }, [costCenterCodeMap]);

  const applyNormalizedItems = useCallback((nextItems) => {
    const normalized = dedupeBy((nextItems || []).map(normalizeStockItem), (item)=>item?.clientId || item?.id || `${item?.farmId||""}::${item?.barcode||""}::${item?.sku||""}::${String(item?.name||"").trim().toLowerCase()}`);
    const enriched = ensureStockRecordNumbers(normalized);
    setItems(enriched);
    return enriched;
  }, [normalizeStockItem, setItems, ensureStockRecordNumbers]);

  useEffect(()=>{
    const exists = (items||[]).some(item=>String(item?.name||'').trim().toLowerCase()==='feno tipo a');
    if(exists) return;
    const haySeed = normalizeStockItem({
      id:createClientId('stk'),
      name:'Feno Tipo A',
      category:'Feno',
      unit:'kg',
      pkgQty:12,
      baleWeightKg:12,
      pkgUnitRef:'kg',
      forageBase:'Tifton 85',
      hayQuality:'Boa',
      guaranteeLevels:'PB 10-14%; FDN 68-76%; FDA 32-38%; Ca 0,35-0,50%; P 0,20-0,30%',
      hayGuaranteeLevels:'PB 10-14%; FDN 68-76%; FDA 32-38%; Ca 0,35-0,50%; P 0,20-0,30%',
      costCenter:'Haras',
      forOrders:true,
      initialQty:0,
      notes:'Produto padrão criado pelo sistema para controlar Feno Tipo A por fardo, forrageira e qualidade.',
    });
    applyNormalizedItems([...(items||[]), haySeed]);
    syncOnSave('stock');
  }, [items, normalizeStockItem, syncOnSave, applyNormalizedItems]);

  const applyNormalizedMovements = useCallback((nextMovements) => {
    const normalized = dedupeBy((nextMovements || []).map(normalizeMovement), (mov)=>mov?.clientId || mov?.id || `${mov?.farmId||""}::${mov?.itemId||""}::${mov?.type||""}::${mov?.ts||mov?.updatedAt||""}::${mov?.qty||0}`);
    setMovements(normalized);
    return normalized;
  }, [normalizeMovement, setMovements]);

  const stockOrphanRecoveryRef = useRef("");

  const extractStockRefsFromOrder = useCallback((order)=>{
    if (!order || order._deleted || order.isDeleted || order.deletedAt || order.deleted_at) return [];
    const result = [];
    const add = (entry = {}) => {
      const stockId = String(entry.stockItemId || entry.itemId || entry.stockId || entry.id || "").trim();
      if (!stockId || !stockId.startsWith("stk_")) return;
      result.push({
        id: stockId,
        name: String(entry.description || entry.name || entry.productName || entry.itemName || entry.label || "").trim(),
        type: String(entry.type || entry.productType || "").trim(),
        category: String(entry.category || "").trim(),
        subcategory: String(entry.subcategory || "").trim(),
        unit: String(entry.unit || entry.unitLabel || "").trim(),
        costCenter: String(entry.costCenter || order.costCenter || "").trim(),
        supplier: String(entry.supplier || order.supplier || order.supplierName || "").trim(),
        supplierId: String(entry.supplierId || order.supplierId || "").trim(),
        brand: String(entry.brand || "").trim(),
        unitCost: Number(entry.price || entry.unitCost || entry.lastPrice || 0) || 0,
        sourceOrderNumber: String(order.recordNumber || order.number || order.id || "").trim(),
      });
    };
    (Array.isArray(order.items) ? order.items : []).forEach(add);
    (Array.isArray(order.costCenterSections) ? order.costCenterSections : []).forEach(section => {
      (Array.isArray(section?.items) ? section.items : []).forEach(item => add({ ...item, costCenter: item?.costCenter || section?.costCenter || order.costCenter }));
    });
    return result;
  }, []);

  // v3.9.14: recuperação de produtos órfãos.
  // Se pedidos/movimentações referenciam um stockItemId, mas o catálogo global stock
  // não possui o produto, recria o cadastro pai de forma idempotente e envia o global.
  // v3.9.15: respeita exclusões/tombstones e separa nome/marca quando possível.
  useEffect(()=>{
    if (!currentFarmId) return;
    const allItems = Array.isArray(items) ? items : [];
    const isDeletedStockItem = (item) => !!(item?._deleted || item?.isDeleted || item?.deletedAt || item?.deleted_at || item?.pendingLocalDelete);
    const activeItems = allItems.filter(item => !isDeletedStockItem(item));
    const existingIds = new Set(activeItems.map(item => String(item?.id || item?.clientId || "").trim()).filter(Boolean));
    // v3.9.15: deleção intencional vence recuperação automática.
    // Se o tombstone estiver disponível localmente, não recriar o produto a partir do histórico.
    const deletedIds = new Set(allItems.filter(isDeletedStockItem).map(item => String(item?.id || item?.clientId || "").trim()).filter(Boolean));
    const refsById = new Map();

    const parseRecoveredNameBrand = (ref = {}) => {
      const rawName = String(ref.name || ref.productName || ref.description || ref.itemName || "").trim();
      const rawBrand = String(ref.brand || "").trim();
      if (!rawName) return { name: `Recuperado_${ref.id}`, brand: rawBrand, needsReview: true };
      if (rawBrand) return { name: rawName, brand: rawBrand, needsReview: false };
      // Quando pedidos antigos guardam "Produto, Marca" ou "Produto — Marca",
      // separar a marca sem esconder a incerteza quando não houver nome confiável.
      const separators = [" — ", " – ", " | ", ","];
      for (const sep of separators) {
        if (rawName.includes(sep)) {
          const parts = rawName.split(sep).map(part => part.trim()).filter(Boolean);
          if (parts.length >= 2) {
            return { name: parts[0], brand: parts.slice(1).join(" "), needsReview: false };
          }
        }
      }
      return { name: rawName, brand: rawBrand, needsReview: false };
    };

    (Array.isArray(orders) ? orders : []).forEach(order => {
      extractStockRefsFromOrder(order).forEach(ref => {
        if (!ref.id || existingIds.has(ref.id) || deletedIds.has(ref.id)) return;
        const prev = refsById.get(ref.id) || {};
        refsById.set(ref.id, { ...prev, ...Object.fromEntries(Object.entries(ref).filter(([, value]) => value !== "" && value != null && value !== 0)) });
      });
    });

    (Array.isArray(movements) ? movements : []).forEach(mov => {
      if (!mov || mov._deleted || mov.isDeleted || mov.deletedAt || mov.deleted_at) return;
      const itemId = String(mov.itemId || mov.stockItemId || mov.stockId || "").trim();
      if (!itemId || existingIds.has(itemId) || deletedIds.has(itemId)) return;
      const prev = refsById.get(itemId) || { id: itemId };
      refsById.set(itemId, {
        ...prev,
        id: itemId,
        name: prev.name || mov.itemName || mov.productName || mov.name || mov.description || "",
        brand: prev.brand || mov.brand || "",
        unit: prev.unit || mov.unit || mov.unitLabel || "",
        category: prev.category || mov.category || "",
        subcategory: prev.subcategory || mov.subcategory || "",
        costCenter: prev.costCenter || mov.costCenter || "",
        sourceMovementId: prev.sourceMovementId || mov.id || mov.clientId || "",
      });
    });

    const missingRefs = Array.from(refsById.values()).filter(ref => ref.id && !existingIds.has(ref.id) && !deletedIds.has(ref.id));
    if (!missingRefs.length) return;

    const signature = JSON.stringify(missingRefs.map(ref => ({ id: ref.id, name: ref.name || "", brand: ref.brand || "", category: ref.category || "", unit: ref.unit || "" })).sort((a,b)=>a.id.localeCompare(b.id)));
    if (stockOrphanRecoveryRef.current === signature) return;
    stockOrphanRecoveryRef.current = signature;

    const ts = new Date().toISOString();
    const recovered = missingRefs.map(ref => {
      const parsed = parseRecoveredNameBrand(ref);
      return normalizeStockItem({
        id: ref.id,
        clientId: ref.id,
        farmId: currentFarmId,
        name: parsed.name,
        type: ref.type || ref.category || "",
        category: ref.category || ref.type || parsed.name || "",
        subcategory: ref.subcategory || "",
        unit: ref.unit || "Unid.",
        costCenter: ref.costCenter || COST_CENTERS[0],
        supplier: ref.supplier || "",
        supplierId: ref.supplierId || "",
        brand: parsed.brand || ref.brand || "",
        unitCost: Number(ref.unitCost || 0) || 0,
        forOrders: true,
        minQty: 0,
        notes: parsed.needsReview
          ? `Produto recuperado automaticamente sem nome confiável a partir de pedidos/movimentações históricas${ref.sourceOrderNumber ? ` (${ref.sourceOrderNumber})` : ""}. Revisar manualmente.`
          : `Produto recuperado automaticamente a partir de pedidos/movimentações históricas${ref.sourceOrderNumber ? ` (${ref.sourceOrderNumber})` : ""}.`,
        recoveredFrom: "orders_stock_movements",
        recoveredAt: ts,
        recoveredNeedsReview: !!parsed.needsReview,
        recoveredSourceOrderNumber: ref.sourceOrderNumber || "",
        recoveredSourceMovementId: ref.sourceMovementId || "",
        serverConfirmed: true,
        syncStatus: "synced",
        pendingLocalCreate: false,
        pendingLocalUpdate: false,
        pendingLocalDelete: false,
        createdAt: ts,
        updatedAt: ts,
        updatedBy: session?.name || "Sistema",
      });
    });

    if (!recovered.length) return;
    applyNormalizedItems([...(items || []), ...recovered]);
    syncOnSave("stock");
    try { show(`♻️ ${recovered.length} produto(s) órfão(s) recuperado(s) no estoque.`, "success"); } catch (_) {}
  }, [currentFarmId, items, orders, movements, extractStockRefsFromOrder, normalizeStockItem, applyNormalizedItems, syncOnSave, session?.name, show]);

  const canCreate = canCreateInModule(session,"stock") || canEditInModule(session,"stock");
  const canImport = canCreate;
  const canEdit   = canEditInModule(session,"stock");
  const canMove   = canCreate;
  const allowedCostCenters = React.useMemo(()=>getAllowedCostCentersForModule(session, "stock"), [session]);
  const [,showSt]=useToast(); useBgSyncToast("stock",showSt);
  const[movOrderLink,setMovOrderLink]=useState(null); // order number to show in link modal
  const stockBootstrapRef = useRef("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{
    const normalizedItems = dedupeBy((items || []).map(normalizeStockItem), (item)=>item?.id || `${item?.farmId||""}::${item?.barcode||""}::${item?.sku||""}::${String(item?.name||"").trim().toLowerCase()}`);
    const normalizedMovements = dedupeBy((movements || []).map(normalizeMovement), (mov)=>mov?.clientId || mov?.id || `${mov?.farmId||""}::${mov?.itemId||""}::${mov?.type||""}::${mov?.ts||mov?.updatedAt||""}::${mov?.qty||0}`);
    const signature = JSON.stringify({
      items: normalizedItems.map(i=>({id:i.id,farmId:i.farmId,name:i.name,barcode:i.barcode,sku:i.sku,minQty:i.minQty,unitCost:i.unitCost,photos:(i.photos||[]).length})),
      movements: normalizedMovements.map(m=>({id:m.id,farmId:m.farmId,itemId:m.itemId,type:m.type,qty:m.qty,ts:m.ts,updatedAt:m.updatedAt}))
    });
    if (stockNormalizeRef.current === signature) return;
    stockNormalizeRef.current = signature;
    const itemsChanged = JSON.stringify(items || []) !== JSON.stringify(normalizedItems);
    const movementsChanged = JSON.stringify(movements || []) !== JSON.stringify(normalizedMovements);
    if (itemsChanged) {
      applyNormalizedItems(normalizedItems);
      syncOnSave("stock");
    }
    if (movementsChanged) {
      applyNormalizedMovements(normalizedMovements);
      syncOnSave("stockMovements");
    }
  }, [items, movements, normalizeStockItem, normalizeMovement, syncOnSave, applyNormalizedItems, applyNormalizedMovements]);

  // ── Auto-migrate: if items have quantity but no movements → create saldo_inicial ──
  useEffect(()=>{
    if(movements.length > 0) return; // already have movements, skip migration
    const itemsWithQty = items.filter(i => Number(i.quantity) > 0);
    if(itemsWithQty.length === 0) return;
    const ts = new Date().toISOString();
    const seedMovs = itemsWithQty.map((item) => makeStockMovementEntry({
      itemId: item.id,
      farmId: currentFarmId || item.farmId || "",
      type: "ajuste",
      qty: Number(item.quantity),
      notes: "Saldo inicial migrado",
      by: "Sistema",
      costCenter: item.costCenter || COST_CENTERS[0],
      ts,
      ...makeCreateMeta("Sistema", { updatedAt: ts }),
    }, ts));
    if(seedMovs.length > 0){
      applyNormalizedMovements(seedMovs);
      syncOnSave("stockMovements");
    }
  // eslint-disable-next-line
  }, [items.length, movements.length, currentFarmId, applyNormalizedMovements, makeStockMovementEntry]);

  useEffect(()=>{
    if(!currentFarmId) return;
    const key = `${currentFarmId}:${items.length}:${movements.length}`;
    if (stockBootstrapRef.current === key) return;
    if (movements.length > 0 || items.length === 0) {
      stockBootstrapRef.current = key;
      return;
    }
    stockBootstrapRef.current = key;
    // v3.9.03: removido — bootstrapFarmCollections não deve ser chamado por módulos.
  },[currentFarmId, items.length, movements.length, session?.name, syncCtx]);

  // ── Calculate current quantity from movements ledger ───────────────
  function calcQty(itemId, filterCC) {
    return movements
      .filter(m => String(m.itemId) === String(itemId))
      .filter(m => !filterCC || m.costCenter === filterCC)
      .reduce((sum, m) => {
        if (m.type === "ajuste") return m.qty; // absolute override (inventory count)
        if (m.type === "transferencia") {
          // transferencia has a delta field: "entrada" or "saida"
          return sum + (m.delta === "entrada" ? +1 : -1) * Math.abs(m.qty);
        }
        const movType = STOCK_MOV_TYPES.find(t=>t.id===m.type);
        return sum + (movType?.delta||0) * Math.abs(m.qty);
      }, 0);
  }

  // ── Items enriched with calculated quantity ────────────────────────
  const enrichedItems = items.map(i => ({
    ...i,
    quantity: calcQty(i.id),
  }));
  const visibleItems = enrichedItems.filter(i=>itemMatchesCostCenterAccess(session, "stock", i));

  const lowStock   = visibleItems.filter(i => i.quantity <= i.minQty);

  const supplierOptions = React.useMemo(() => buildSupplierDirectory(people, suppliers)
    .filter((s) => s && String(s.name || s.legalName || "").trim())
    .map((s) => ({ ...s, name: String(s.name || s.legalName || "").trim() }))
    .sort((a,b)=>String(a.name||"").localeCompare(String(b.name||""), "pt-BR")), [people, suppliers]);
  const stockBrandOptions = React.useMemo(() => (SL.STOCK_BRANDS || []).filter(Boolean), [SL.STOCK_BRANDS]);
  const catalogTypeOptions = React.useMemo(() => (Array.isArray(SL.CATALOG_PRODUCT_TYPES) ? SL.CATALOG_PRODUCT_TYPES : []).map(v=>String(v||"").trim()).filter(Boolean), [SL.CATALOG_PRODUCT_TYPES]);
  const catalogCategoryOptions = React.useMemo(() => (Array.isArray(SL.CATALOG_PRODUCT_CATEGORIES) ? SL.CATALOG_PRODUCT_CATEGORIES : []).map(entry=>({
    name: String(entry?.name || entry?.label || "").trim(),
    typeName: String(entry?.typeName || entry?.type || "").trim(),
  })).filter(entry=>entry.name), [SL.CATALOG_PRODUCT_CATEGORIES]);
  const catalogSubcategoryOptions = React.useMemo(() => (Array.isArray(SL.CATALOG_PRODUCT_SUBCATEGORIES) ? SL.CATALOG_PRODUCT_SUBCATEGORIES : []).map(entry=>({
    name: String(entry?.name || entry?.label || "").trim(),
    categoryName: String(entry?.categoryName || entry?.category || "").trim(),
  })).filter(entry=>entry.name), [SL.CATALOG_PRODUCT_SUBCATEGORIES]);
  const getCategoriesForType = React.useCallback((typeName)=> catalogCategoryOptions.filter(entry=>String(entry.typeName||"")===String(typeName||"")).map(entry=>entry.name), [catalogCategoryOptions]);
  const getSubcategoriesForCategory = React.useCallback((categoryName)=> catalogSubcategoryOptions.filter(entry=>String(entry.categoryName||"")===String(categoryName||"")).map(entry=>entry.name), [catalogSubcategoryOptions]);
  const stockFilterOptions = React.useMemo(() => ({
    categories: Array.from(new Set((visibleItems || []).map(item=>String(item?.category||"").trim()).filter(Boolean))).sort((a,b)=>a.localeCompare(b, "pt-BR")),
    brands: Array.from(new Set((visibleItems || []).map(item=>String(item?.brand||"").trim()).filter(Boolean))).sort((a,b)=>a.localeCompare(b, "pt-BR")),
    suppliers: Array.from(new Set((visibleItems || []).map(item=>String(item?.supplier||"").trim()).filter(Boolean))).sort((a,b)=>a.localeCompare(b, "pt-BR")),
    costCenters: Array.from(new Set((visibleItems || []).map(item=>String(item?.costCenter||"").trim()).filter(Boolean))).sort((a,b)=>a.localeCompare(b, "pt-BR")),
    units: Array.from(new Set((visibleItems || []).map(item=>String(item?.unit||"").trim()).filter(Boolean))).sort((a,b)=>a.localeCompare(b, "pt-BR")),
  }), [visibleItems]);
  const activeStockFiltersCount = React.useMemo(() => Object.values(stockFilters).filter(Boolean).length, [stockFilters]);
  const filteredStockItems = React.useMemo(() => {
    const q = String(stockSearchText || "").trim().toLowerCase();
    const base = visibleItems.filter((item) => {
      const matchesSearch = matchesRecordNumberSearch(item, q, [item?.name, item?.brand, item?.supplier, item?.type, item?.category, item?.subcategory, item?.unit, item?.costCenter]);
      if (!matchesSearch) return false;
      if (stockFilters.category && item.category !== stockFilters.category) return false;
      if (stockFilters.brand && item.brand !== stockFilters.brand) return false;
      if (stockFilters.supplier && item.supplier !== stockFilters.supplier) return false;
      if (stockFilters.costCenter && item.costCenter !== stockFilters.costCenter) return false;
      if (stockFilters.unit && item.unit !== stockFilters.unit) return false;
      if (stockFilters.stockState === "low" && !(Number(item.quantity||0) <= Number(item.minQty||0))) return false;
      if (stockFilters.stockState === "withBalance" && !(Number(item.quantity||0) > 0)) return false;
      if (stockFilters.stockState === "noBalance" && !(Number(item.quantity||0) <= 0)) return false;
      return true;
    });
    return base.slice().sort(compareStockByLastActivity);
  }, [visibleItems, stockFilters, stockSearchText, compareStockByLastActivity]);
  const stockFilterSummary = React.useMemo(() => {
    const labels = [];
    if (stockFilters.category) labels.push(`Categoria: ${stockFilters.category}`);
    if (stockFilters.brand) labels.push(`Marca: ${stockFilters.brand}`);
    if (stockFilters.supplier) labels.push(`Fornecedor: ${stockFilters.supplier}`);
    if (stockFilters.costCenter) labels.push(`Centro: ${stockFilters.costCenter}`);
    if (stockFilters.unit) labels.push(`Unidade: ${stockFilters.unit}`);
    if (stockFilters.stockState === "low") labels.push("Estoque baixo");
    if (stockFilters.stockState === "withBalance") labels.push("Com saldo");
    if (stockFilters.stockState === "noBalance") labels.push("Sem saldo");
    return labels;
  }, [stockFilters]);
  const stockUnitOptions = React.useMemo(() => ((SL.STOCK_UNITS || STOCK_UNITS || []).map((entry) => {
    if (typeof entry === "string") return { label: entry, defaultQty: "", refUnit: "" };
    return {
      label: String(entry?.label || entry?.name || entry?.unit || "").trim(),
      defaultQty: entry?.defaultQty == null ? "" : String(entry.defaultQty),
      refUnit: String(entry?.refUnit || entry?.baseUnit || entry?.referenceUnit || "").trim(),
    };
  }).filter(opt => opt.label)), [SL.STOCK_UNITS]);
  const getUnitConfig = React.useCallback((unitLabel) => stockUnitOptions.find(opt => String(opt.label) === String(unitLabel || "")) || null, [stockUnitOptions]);

  const STOCK_CARD_LIBRARY = React.useMemo(()=>[
    { id:"duracao_racao_bovinos", label:"Duração de Ração", category:"Ração", species:"Bovino", subLabel:"Bovinos", icon:renderCardImageIcon(bovineRationCardIcon, "Duração de ração bovinos") },
    { id:"duracao_suplemento_bovinos", label:"Duração de Suplemento", category:"Suplementação", species:"Bovino", subLabel:"Bovinos" },
    { id:"duracao_racao_equinos", label:"Duração de Ração", category:"Ração", species:"Equino", subLabel:"Equinos", icon:renderCardImageIcon(equineRationCardIcon, "Duração de ração equinos") },
    { id:"duracao_suplemento_equinos", label:"Duração de Suplemento", category:"Suplementação", species:"Equino", subLabel:"Equinos" },
    { id:"duracao_racao_aves_postura", label:"Duração de Ração", category:"Ração", species:"Ave", subcategory:"Aves Postura", subLabel:"Aves Postura", icon:renderCardImageIcon(genericRationCardIcon, "Duração de ração aves postura") },
    { id:"duracao_racao_aves_crescimento", label:"Duração de Ração", category:"Ração", species:"Ave", subcategory:"Aves Crescimento", subLabel:"Aves Crescimento", icon:renderCardImageIcon(genericRationCardIcon, "Duração de ração aves crescimento") },
    { id:"duracao_feno_equinos", label:"Duração de Feno", category:"Feno", species:"Equino", subLabel:"Equinos", icon:renderCardImageIcon(hayBaleCardIcon, "Duração de feno") },
    { id:"qtd_palha_arroz", label:"Qtd. Palha de Arroz", nameIncludes:"palha de arroz", subLabel:"Estoque atual" },
  ], []);
  useEffect(()=>{
    if(initialTab) setTab(initialTab);
  }, [initialTab]);

  const stockCardsConfig = React.useMemo(()=>{
    const defaults = ["duracao_racao_bovinos","duracao_suplemento_bovinos","duracao_racao_equinos","duracao_racao_aves_postura"];
    const menuCards = getConfiguredSectionCards("menu:estoque", defaults);
    const homeCards = getConfiguredSectionCards("moduleHome", defaults);
    const chosen = (Array.isArray(menuCards) && menuCards.length ? menuCards : (Array.isArray(homeCards) && homeCards.length ? homeCards : defaults));
    const allowedIds = new Set(["duracao_racao_bovinos","duracao_suplemento_bovinos","duracao_racao_equinos","duracao_racao_aves_postura","duracao_suplemento_equinos","duracao_racao_aves_crescimento","duracao_feno_equinos","qtd_palha_arroz"]);
    const normalized = Array.from(new Set((chosen || []).map(id=>String(id||"")).filter(id=>allowedIds.has(id))));
    return (normalized.length ? normalized : defaults).slice(0,4);
  }, [getConfiguredSectionCards]);

  const animalSpeciesBaseOptions = React.useMemo(()=>["Ave","Bovino","Ovino","Equino"], []);
  const posologySpeciesOptions = React.useMemo(()=>{
    const enabled = Array.isArray(farm?.enabledAnimalSpecies) ? farm.enabledAnimalSpecies : [];
    const normalized = enabled.map(v=>String(v||"").trim()).filter(Boolean);
    const allowed = normalized.filter(v=>animalSpeciesBaseOptions.includes(v));
    return allowed.length ? allowed : animalSpeciesBaseOptions;
  }, [farm?.enabledAnimalSpecies, animalSpeciesBaseOptions]);
  const posologyCategoryOptions = React.useCallback((species)=>{
    const options = getAnimalCategoryOptions(species);
    return options.length ? options : ["Geral"];
  }, []);
  const guaranteeUnitOptions = ["g/kg","mg/kg","mL/kg","UI/kg","%","ppm","g","mg","mL","kg","Outro"];
  const createGuaranteeDraft = ()=>({ id:createClientId("gar"), nutrient:"", amount:"", unit:"g/kg", notes:"" });
  const parseGuaranteeText = (value="")=>{
    const parts = String(value||"").split(/\n|;/).map(v=>String(v||"").trim()).filter(Boolean);
    return parts.map((entry, idx)=>{
      const match = entry.match(/^([^:]+):?\s*([0-9.,]+)?\s*(.*)$/i);
      return {
        id: createClientId(`gar${idx}`),
        nutrient: String(match?.[1] || entry).trim(),
        amount: String(match?.[2] || "").replace(',', '.'),
        unit: String(match?.[3] || "g/kg").trim() || "g/kg",
        notes: "",
      };
    });
  };
  const formatGuaranteeItems = (items=[])=> (Array.isArray(items)?items:[]).map(item=>{
    const nutrient = String(item?.nutrient||"").trim();
    const amount = String(item?.amount??"").trim();
    const unit = String(item?.unit||"").trim();
    if(!nutrient) return "";
    return `${nutrient}: ${[amount, unit].filter(Boolean).join(" ")}`.trim();
  }).filter(Boolean).join("; ");
  const createProfileDraft = (kind="nutrition", species="Ave", category=null)=>({
    id:createClientId(kind==="nutrition"?"npr":"pso"),
    species,
    category:(category || posologyCategoryOptions(species)[0]||"Geral"),
    sex:"Ambos",
    allowed:true,
    dosageMode: kind === "nutrition" ? "perAnimal" : "mlPerKg",
    basis: kind === "nutrition" ? "perAnimal" : "perKgLiveweight",
    dose:"",
    doseUnit: kind === "nutrition" ? "kg" : "mL",
    referenceWeightKg:"",
    notes:"",
  });
  const ensureProfileByCategory = (profiles=[], kind="nutrition", species="Ave", category="Geral", checked=true)=>{
    const list = Array.isArray(profiles) ? [...profiles] : [];
    const idx = list.findIndex(profile=>String(profile?.species||"")===String(species||"") && String(profile?.category||"")===String(category||""));
    if(checked){
      if(idx === -1) list.push(createProfileDraft(kind, species, category));
      return list;
    }
    if(idx >= 0) list.splice(idx, 1);
    return list;
  };

  const defaultCatalogType = catalogTypeOptions[0] || "";
  const defaultCatalogCategory = getCategoriesForType(defaultCatalogType)[0] || "";
  const defaultCatalogSubcategory = getSubcategoriesForCategory(defaultCatalogCategory)[0] || "";
  const defForm = {
    _new:true, name:"", brand:"", sku:"", barcode:"",
    type:defaultCatalogType, category:defaultCatalogCategory, subcategory:defaultCatalogSubcategory, unit:(stockUnitOptions[0]?.label || STOCK_UNITS[0]),
    pkgQty:(stockUnitOptions[0]?.defaultQty || ""), pkgUnitRef:(stockUnitOptions[0]?.refUnit || ""), minQty:0, costCenter:allowedCostCenters[0] || COST_CENTERS[0],
    unitCost:0, supplier:"", supplierId:"", photo:"",
    notes:"", initialQty:0,
    vetUse:false, forOrders:true, controlled:false,
    targetSpecies:[],
    guaranteeItems:[],
    nutritionProfiles:[],
    posologyProfiles:[],
  };
  const [speciesProfileFocus, setSpeciesProfileFocus] = useState("");
  useEffect(()=>{ setEditingFlag?.(!!form); return ()=>setEditingFlag?.(false); },[form]);
  useEffect(()=>{
    const available = Array.isArray(form?.targetSpecies) ? form.targetSpecies.filter(Boolean) : [];
    if(!available.length){
      if(speciesProfileFocus) setSpeciesProfileFocus("");
      return;
    }
    if(!available.includes(speciesProfileFocus)) setSpeciesProfileFocus(available[0] || "");
  }, [form?.targetSpecies, speciesProfileFocus]);

  function applyUnitDefaults(unitLabel, { preserveManualQty = false, preserveManualRef = false } = {}) {
    const unitCfg = getUnitConfig(unitLabel);
    setForm(f => ({
      ...f,
      unit: unitLabel,
      pkgQty: preserveManualQty && f?.pkgQty !== "" && f?.pkgQty != null ? f.pkgQty : (unitCfg?.defaultQty || ""),
      pkgUnitRef: preserveManualRef && String(f?.pkgUnitRef || "").trim() ? f.pkgUnitRef : (unitCfg?.refUnit || ""),
    }));
  }

  function saveItem() {
    if (form?._new && !canImport) { show("Sem permissão para criar itens de estoque.", "error"); return; }
    if (!form?._new && !canEdit) { show("Sem permissão para editar itens de estoque.", "error"); return; }
    if (!form.name.trim()) { show("Nome obrigatório.", "error"); return; }
    if (!canEditCostCenter(session, "stock", form?.costCenter || allowedCostCenters[0] || COST_CENTERS[0])) { show("Sem permissão para editar este centro de custo.", "error"); return; }
    const { _new, initialQty, ...d } = form;
    const ts = new Date().toISOString();
    const selectedSupplier = supplierOptions.find(s => String(s.id) === String(d.supplierId || "")) || supplierOptions.find(s => String(s.name||"") === String(d.supplier||""));
    const normalizedDraft = {
      ...d,
      supplierId: selectedSupplier?.id || d.supplierId || "",
      supplier: selectedSupplier?.name || d.supplier || "",
      type: String(d.type || '').trim(),
      category: String(d.category || '').trim(),
      subcategory: String(d.subcategory || '').trim(),
      targetSpecies: Array.isArray(d.targetSpecies) ? d.targetSpecies.map(v=>String(v||"").trim()).filter(Boolean) : [],
      guaranteeItems: (Array.isArray(d.guaranteeItems) ? d.guaranteeItems : []).map(entry=>({
        id: entry?.id || createClientId("gar"),
        nutrient: String(entry?.nutrient||"").trim(),
        amount: entry?.amount ?? "",
        unit: String(entry?.unit||"").trim() || "g/kg",
        notes: String(entry?.notes||"").trim(),
      })).filter(entry=>entry.nutrient),
      nutritionProfiles: (Array.isArray(d.nutritionProfiles) ? d.nutritionProfiles : []).map(entry=>({
        id: entry?.id || createClientId("npr"),
        species: String(entry?.species||"").trim(),
        category: String(entry?.category||"").trim(),
        sex: String(entry?.sex||"Ambos").trim() || "Ambos",
        basis: String(entry?.basis||"perAnimal").trim() || "perAnimal",
        dose: entry?.dose ?? "",
        doseUnit: String(entry?.doseUnit||"kg").trim() || "kg",
        referenceWeightKg: entry?.referenceWeightKg ?? "",
        notes: String(entry?.notes||"").trim(),
      })).filter(entry=>entry.species),
      posologyProfiles: (Array.isArray(d.posologyProfiles) ? d.posologyProfiles : []).map(entry=>({
        id: entry?.id || createClientId("pso"),
        species: String(entry?.species||"").trim(),
        category: String(entry?.category||"").trim(),
        sex: String(entry?.sex||"Ambos").trim() || "Ambos",
        dosageMode: String(entry?.dosageMode||"mlPerKg").trim() || "mlPerKg",
        basis: String(entry?.basis || (String(entry?.dosageMode||"mlPerKg") === "singleDose" ? "perAnimal" : "perKgLiveweight")).trim(),
        dose: entry?.dose ?? "",
        doseUnit: String(entry?.doseUnit || (String(entry?.dosageMode||"mlPerKg") === "singleDose" ? "dose" : "mL")).trim(),
        referenceWeightKg: entry?.referenceWeightKg ?? "",
        allowed: entry?.allowed !== false,
        notes: String(entry?.notes||"").trim(),
      })).filter(entry=>entry.species),
    };
    if(normalizedDraft.type === "Medicamento"){
      normalizedDraft.guaranteeItems = [];
      normalizedDraft.guaranteeLevels = "";
      normalizedDraft.hayGuaranteeLevels = "";
    } else {
      normalizedDraft.guaranteeLevels = formatGuaranteeItems(normalizedDraft.guaranteeItems);
    }
    if(String(normalizedDraft.category||"").toLowerCase() === 'feno' && normalizedDraft.guaranteeLevels){
      normalizedDraft.hayGuaranteeLevels = normalizedDraft.guaranteeLevels;
    }
    if (_new) {
      const newId = createClientId("stk");
      const baseNewItem = {
        ...normalizedDraft,
        id: newId,
        clientId: newId,
        farmId: normalizedDraft.farmId || currentFarmId || "",
        ...makeProvisionalStockRecordNumber(normalizedDraft),
        ...makeCreateMeta(session?.name || "", { updatedAt: ts })
      };
      const newItem = markStockCreatePending(baseNewItem, ts);
      const updatedItems = [...items, newItem];
      applyNormalizedItems(updatedItems);
      if (Number(initialQty) > 0) {
        const mov = makeStockMovementEntry({
          itemId: newId,
          farmId: currentFarmId || normalizedDraft.farmId || "",
          type: "ajuste",
          qty: Number(initialQty),
          notes: "Saldo inicial",
          by: session?.name || "admin",
          costCenter: d.costCenter,
          ts,
          ...makeCreateMeta(session?.name || "", { updatedAt: ts }),
        }, ts);
        applyNormalizedMovements([...(movements || []), mov]);
        syncOnSave("stockMovements");
      }
    } else {
      const updatedItems = items.map(i => i.id===d.id ? markStockUpdatePending({
        ...i,
        ...normalizedDraft,
        id: i?.id || d.id,
        clientId: i?.clientId || i?.id || d.id,
        farmId: i?.farmId || normalizedDraft.farmId || currentFarmId || "",
        ...makeUpdateMeta(session?.name || "", { updatedAt: ts })
      }, ts) : i);
      applyNormalizedItems(updatedItems);
    }
    show("✅ Salvo!"); setForm(null); syncOnSave("stock");
  }

  // ── Register a movement ────────────────────────────────────────────
  function saveMovement() {
    if (!canMove) { show("Sem permissão para movimentar estoque.","error"); return; }
    if (!movForm.itemId) { show("Selecione o item.","error"); return; }
    if (!canEditCostCenter(session, "stock", movForm?.costCenter || allowedCostCenters[0] || COST_CENTERS[0])) { show("Sem permissão para movimentar este centro de custo.", "error"); return; }
    if (!movForm.qty || Number(movForm.qty) <= 0) { show("Quantidade deve ser maior que zero.","error"); return; }
    if (movForm.type === "transferencia" && !movForm.destCostCenter) {
      show("Selecione o centro de custo de destino.","error"); return;
    }
    const ts = new Date().toISOString();
    const item = visibleItems.find(i=>String(i.id)===String(movForm.itemId));
    // Validate stock for outgoing movements
    if (!["ajuste","entrada","nfe"].includes(movForm.type)) {
      const afterQty = item ? item.quantity - Number(movForm.qty) : 0;
      if (afterQty < 0) {
        show(`⚠️ Estoque insuficiente. Disponível: ${item?.quantity||0} ${item?.unit||"un"}`, "error");
        return;
      }
    }
    const newMovs = [];

    if (movForm.type === "transferencia") {
      // Create two entries: saída do CC origem + entrada no CC destino
      newMovs.push(makeStockMovementEntry({
        itemId: movForm.itemId,
        farmId: currentFarmId || item?.farmId || "",
        type: "transferencia",
        delta: "saida",
        qty: Number(movForm.qty),
        notes: `Transferência → ${movForm.destCostCenter}. ${movForm.notes||""}`.trim(),
        by: session?.name || "—",
        costCenter: movForm.costCenter || item?.costCenter || COST_CENTERS[0],
        destCostCenter: movForm.destCostCenter,
        ts, updatedAt: ts,
      }, ts));
      newMovs.push(makeStockMovementEntry({
        itemId: movForm.itemId,
        farmId: currentFarmId || item?.farmId || "",
        type: "transferencia",
        delta: "entrada",
        qty: Number(movForm.qty),
        notes: `Transferência ← ${movForm.costCenter||item?.costCenter}. ${movForm.notes||""}`.trim(),
        by: session?.name || "—",
        costCenter: movForm.destCostCenter,
        sourceCostCenter: movForm.costCenter || item?.costCenter,
        ts, updatedAt: ts,
      }, ts));
    } else {
      newMovs.push(makeStockMovementEntry({
        itemId: movForm.itemId,
        farmId: currentFarmId || item?.farmId || "",
        type: movForm.type || "saida",
        qty: Number(movForm.qty),
        notes: movForm.notes || "",
        by: session?.name || "—",
        costCenter: movForm.costCenter || item?.costCenter || COST_CENTERS[0],
        ts, updatedAt: ts,
      }, ts));
    }

    applyNormalizedMovements([...(movements || []), ...newMovs]);
    show("✅ Movimentação registrada!"); setMovForm(null); syncOnSave("stockMovements");
  }

  // ── NF-e file load ──────────────────────────────────────────────────────────
  function handleXmlFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setNfeLoading(true);
    const reader = new FileReader();
    reader.onload = ev => {
      const result = parseNFe(ev.target.result);
      if (result.error) { show(result.error, "error"); setNfeLoading(false); e.target.value=""; return; }
      // Build preview rows: for each product find existing stock item by name similarity
      const preview = result.prods.map((p,idx) => {
        const existing = items.find(i => i.name.toLowerCase().trim() === p.name.toLowerCase().trim());
        return {
          idx, name:p.name, unit:mapUnit(p.unit), qty:p.qty,
          unitVal:p.unitVal, total:p.total, ncm:p.ncm,
          existingId: existing?.id || null,
          existingName: existing?.name || null,
          existingQty: existing?.quantity || 0,
          lastPrice: existing?.unitCost || 0,
          category: existing?.category || getCategoriesForType(existing?.type || '')[0] || '',
          costCenter: existing?.costCenter || COST_CENTERS[0],
          action: existing ? "update" : "create",  // update = add to existing, create = new item
        };
      });
      setNfeData(result);
      setNfePreview(preview);
      setNfeLoading(false);
      e.target.value="";
    };
    reader.readAsText(file, "UTF-8");
  }

  // ── Confirm NF-e import ─────────────────────────────────────────────────────
  function confirmImport() {
    let updatedItems = [...items];
    const ts = new Date().toISOString();

    nfePreview.forEach(p => {
      if (p.action === "skip") return;
      if (p.action === "update" && p.existingId) {
        updatedItems = updatedItems.map(i => i.id === p.existingId
          ? markStockUpdatePending({ ...i, unitCost: p.unitVal, lastPrice: i.unitCost, lastPriceDate: today() }, ts)
          : i
        );
      } else if (p.action === "create") {
        const newStockId = createClientId("stk");
        const baseStockItem = {
          id: newStockId, clientId: newStockId, name: p.name, category: p.category,
          unit: p.unit, quantity: 0, minQty: 0,
          costCenter: p.costCenter, unitCost: p.unitVal,
          lastPrice: null, lastPriceDate: null, notes: p.ncm ? `NCM: ${p.ncm}` : "",
          vetUse: !!p.vetUse,
          ...makeProvisionalStockRecordNumber({ costCenter:p.costCenter }),
          ...makeCreateMeta(session?.name || "", { updatedAt: ts }),
        };
        updatedItems = [...updatedItems, markStockCreatePending(baseStockItem, ts)];
      }
    });

    // In event-sourcing model, NF-e creates movement records instead of mutating quantity
    // Build movement records for each imported product
    const newMovements = [];
    nfePreview.forEach(p => {
      if (p.action === "skip") return;
      const itemId = p.action === "update" ? p.existingId
        : updatedItems.find(i=>i.name===p.name)?.id;
      if (!itemId) return;
      newMovements.push(makeStockMovementEntry({
        itemId,
        type: "nfe",
        qty: p.qty,
        notes: `NF-e ${nfeData.numero||""} — ${nfeData.nomeEmit||""}`,
        by: session?.name || "admin",
        costCenter: p.costCenter || COST_CENTERS[0],
        unitCost: p.unitVal,
        ts,
        updatedAt: ts,
      }, ts));
    });
    // Only update catalog items (names, costs) — not quantities
    applyNormalizedItems(updatedItems.map(i=>({...i, updatedAt:ts})));
    if(newMovements.length>0){
      applyNormalizedMovements([...(movements || []), ...newMovements]);
      syncOnSave("stockMovements");
    }

    // Save invoice record
    const invoice = {
      id: createClientId("inv"),
      numero: nfeData.numero, serie: nfeData.serie,
      emissao: nfeData.emissao, natOp: nfeData.natOp,
      fornecedor: nfeData.nomeEmit, cnpj: nfeData.cnpjEmit,
      totalNF: nfeData.totalNF,
      importedAt: today(),
      prods: nfePreview.filter(p=>p.action!=="skip").map(p=>({
        name:p.name, qty:p.qty, unit:p.unit, unitVal:p.unitVal, total:p.total, action:p.action
      })),
    };
    setInvoices(p => [...p, invoice]);
    setNfeData(null); setNfePreview(null);
    show(`✅ NF-e ${nfeData.numero} importada! ${nfePreview.filter(p=>p.action!=="skip").length} produto(s) atualizados.`);
    setTab("invoices");
  }

  function updatePreviewRow(idx, field, value) {
    setNfePreview(p => p.map(r => r.idx===idx ? {...r,[field]:value} : r));
  }

  const stockCardSummaries = React.useMemo(()=>{
    const normalizedKey = (value)=>String(value||"").trim().toLowerCase();
    const normalizedIncludes = (value, query)=>normalizedKey(value).includes(normalizedKey(query));
    const fmtMetric = (value, digits=2)=>{
      const num = Number(value||0);
      return new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: digits }).format(Number.isFinite(num) ? num : 0);
    };
    const getPackKg = (item)=>{
      const unit = normalizedKey(item?.unit);
      const refUnit = normalizedKey(item?.pkgUnitRef);
      const pkgQty = Number(item?.pkgQty || 0);
      if(unit === "kg" || unit.includes("kg")) return 1;
      if((unit.includes("saco") || unit.includes("fardo") || unit.includes("pacote")) && pkgQty > 0 && refUnit.includes("kg")) return pkgQty;
      if(pkgQty > 0 && refUnit.includes("kg")) return pkgQty;
      return null;
    };
    const getCurrentLotQty = (lot)=>{
      const mortality = (lot?.mortalityEntries||[]).reduce((sum, entry)=>sum + Math.max(0, Number(entry?.qty||0)), 0);
      const transfers = (lot?.transfersOut||[]).reduce((sum, entry)=>sum + Math.max(0, Number(entry?.qty||0)), 0);
      return Math.max(0, Number(lot?.quantity||0) - mortality - transfers);
    };
    const itemSupportsSpeciesCategory = (item, species, category)=>{
      const targetSpecies = Array.isArray(item?.targetSpecies) ? item.targetSpecies.map(v=>String(v||"").trim()) : [];
      const profiles = Array.isArray(item?.nutritionProfiles) ? item.nutritionProfiles : [];
      if(targetSpecies.length && !targetSpecies.includes(species)) return false;
      if(!category) return targetSpecies.length ? targetSpecies.includes(species) : profiles.length ? profiles.some(profile=>String(profile?.species||"")===String(species)) : true;
      if(profiles.length){
        return profiles.some(profile=>String(profile?.species||"")===String(species) && String(profile?.category||"")===String(category));
      }
      return true;
    };
    const profileDosePerHeadKg = (item, species, category)=>{
      const profiles = Array.isArray(item?.nutritionProfiles) ? item.nutritionProfiles : [];
      const profile = profiles.find(profile=>String(profile?.species||"")===String(species) && (!category || String(profile?.category||"")===String(category)))
        || profiles.find(profile=>String(profile?.species||"")===String(species))
        || null;
      return Number(profile?.dose || 0);
    };
    const dailySacksForBovino = (item, kind)=>{
      return (animalLots||[])
        .filter(lot=>String(lot?.species||"")==='Bovino')
        .reduce((sum, lot)=>{
          const qty = Math.max(0, Number((lot?.currentQty ?? lot?.quantity) || 0));
          if(!qty) return sum;
          const list = kind === 'ration' ? (lot?.rationPlans||[]) : (lot?.supplements||[]);
          const matched = list.filter(entry=>String(entry?.stockId||"")===String(item?.id||""));
          if(!matched.length) return sum;
          const kgPerHead = matched.reduce((acc, entry)=>acc + Number(entry?.dailyKg||0), 0);
          const packKg = getPackKg(item);
          if(!(packKg > 0) || !(kgPerHead > 0)) return sum;
          return sum + ((kgPerHead * qty) / packKg);
        }, 0);
    };
    const dailySacksForEquino = (item)=>{
      const activeServiceCount = (animals||[]).filter(animal=>String(animal?.species||"")==='Equino' && String(animal?.equinoCategory||"")==='service' && !!animal?.rationConsumer && String(animal?.status||'Ativo')!=='Inativo').length;
      const dosePerHead = profileDosePerHeadKg(item, 'Equino', 'Animais de Serviço');
      const packKg = getPackKg(item);
      if(activeServiceCount > 0 && dosePerHead > 0 && packKg > 0) return (activeServiceCount * dosePerHead) / packKg;
      const latest = (serviceNutritionEntries||[])
        .filter(entry=>String(entry?.stockId||"")===String(item?.id||""))
        .slice()
        .sort((a,b)=>String(b?.ts||b?.createdAt||"").localeCompare(String(a?.ts||a?.createdAt||"")))[0];
      if(!latest) return 0;
      const qty = Number(latest?.qty||0);
      const period = normalizedKey(latest?.period);
      if(period.includes('sem')) return qty / 7;
      if(period.includes('m')) return qty / 30;
      return qty;
    };
    const dailySacksForAvesPostura = (item)=>{
      const posturaLots = (animalLots||[]).filter(lot=>String(lot?.species||"")==='Ave' && String(lot?.birdStage||"")==='postura');
      const packKg = getPackKg(item);
      const dosePerHead = profileDosePerHeadKg(item, 'Ave', 'Aves Postura');
      if(packKg > 0 && dosePerHead > 0){
        const totalBirds = posturaLots.reduce((sum, lot)=>sum + getCurrentLotQty(lot), 0);
        if(totalBirds > 0) return (totalBirds * dosePerHead) / packKg;
      }
      const latest = (birdLotsNutrition||[])
        .filter(entry=>String(entry?.stockId||"")===String(item?.id||""))
        .filter(entry=>String(entry?.stageKey||entry?.animalCategory||"").toLowerCase().includes('postura'))
        .slice()
        .sort((a,b)=>String(b?.ts||b?.createdAt||"").localeCompare(String(a?.ts||a?.createdAt||"")))[0];
      if(!latest) return 0;
      const qty = Number(latest?.qty||0);
      const period = normalizedKey(latest?.period);
      if(period.includes('sem')) return qty / 7;
      if(period.includes('m')) return qty / 30;
      return qty;
    };
    const calcDailySacksDemand = (card, item)=>{
      if(card.id === 'duracao_racao_bovinos') return dailySacksForBovino(item, 'ration');
      if(card.id === 'duracao_suplemento_bovinos') return dailySacksForBovino(item, 'supplement');
      if(card.id === 'duracao_racao_equinos') return dailySacksForEquino(item);
      if(card.id === 'duracao_suplemento_equinos') return dailySacksForEquino(item);
      if(card.id === 'duracao_feno_equinos') return dailySacksForEquino(item);
      if(card.id === 'duracao_racao_aves_postura') return dailySacksForAvesPostura(item);
      if(card.id === 'duracao_racao_aves_crescimento') return dailySacksForAvesPostura(item);
      return 0;
    };

    const selectedDefs = stockCardsConfig.map(id=>STOCK_CARD_LIBRARY.find(card=>card.id===id)).filter(Boolean);
    return selectedDefs.map(card=>{
      let candidates = enrichedItems.filter(item=>Number(item.quantity||0) > 0);
      if(card.nameIncludes){
        candidates = candidates.filter(item=>normalizedIncludes(item?.name, card.nameIncludes));
        const qty = candidates.reduce((sum,item)=>sum + Number(item.quantity||0), 0);
        return { ...card, value:`${fmtMoney(qty)} ${candidates[0]?.unit||"un"}`, helper:candidates.length?"Quantidade disponível":"Nenhum item encontrado" };
      }
      candidates = candidates.filter(item=>normalizedIncludes(item?.category, card.category));
      if(card.species){
        candidates = candidates.filter(item=>itemSupportsSpeciesCategory(item, card.species, card.subcategory || null));
      }
      const ranked = candidates
        .map(item=>({ item, dailySacks: calcDailySacksDemand(card, item) }))
        .sort((a,b)=>{
          if(Number(b.dailySacks||0) !== Number(a.dailySacks||0)) return Number(b.dailySacks||0) - Number(a.dailySacks||0);
          return Number(b.item?.quantity||0) - Number(a.item?.quantity||0);
        });
      const bestMatch = ranked[0] || null;
      const best = bestMatch?.item;
      if(!best) return { ...card, value:'—', helper:'Sem produto compatível' };
      const dailySacks = Number(bestMatch?.dailySacks||0);
      if(dailySacks > 0){
        const currentSacks = Number(best.quantity||0);
        const days = currentSacks / dailySacks;
        return {
          ...card,
          itemName: best.name,
          value: `${fmtMetric(days,1)} dias`,
          helper: `${best.name} · estoque ${fmtMetric(currentSacks)} ${best.unit||'un'} · consumo ${fmtMetric(dailySacks,3)} saco(s)/dia`,
        };
      }
      return { ...card, itemName:best.name, value:`${fmtMetric(best.quantity||0)} ${best.unit||"un"}`, helper:`${best.name} · sem consumo diário correlacionado` };
    });
  }, [stockCardsConfig, STOCK_CARD_LIBRARY, enrichedItems, animalLots, animals, serviceNutritionEntries, birdLotsNutrition]);


  const inlinePanelStyle = {marginTop:16,background:T.card,border:`1px solid ${T.border}`,borderRadius:18,padding:isCompactViewport()?16:20,boxShadow:`0 12px 28px ${T.shadow || "rgba(0,0,0,.08)"}`};
  const renderInlineMovementForm = ()=> movForm ? <div style={inlinePanelStyle}><div style={{display:"flex",flexDirection:"column",gap:14}}>
          <Field label="Tipo">
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))",gap:6}}>
              {STOCK_MOV_TYPES.filter(t=>canImport||t.id==="saida").map(t=>(
                <div key={t.id} onClick={()=>setMovForm(f=>({...f,type:t.id}))}
                  style={{padding:"8px 10px",borderRadius:8,cursor:"pointer",textAlign:"center",
                    border:`2px solid ${movForm.type===t.id?t.color:T.border}`,
                    background:movForm.type===t.id?`${t.color}18`:T.card2,transition:"all .15s"}}>
                  <div style={{fontSize:16,marginBottom:2}}>{t.icon}</div>
                  <div style={{fontSize:11,fontWeight:movForm.type===t.id?700:400,
                    color:movForm.type===t.id?t.color:T.muted}}>{t.label}</div>
                </div>
              ))}
            </div>
          </Field>
          <Field label="Produto *">
            <Select value={movForm.itemId||""} onChange={e=>setMovForm(f=>({...f,itemId:e.target.value}))}>
              <option value="">Selecione o produto...</option>
              {enrichedItems.map(i=>(
                <option key={i.id} value={i.id}>
                  {i.name}{i.brand?` — ${i.brand}`:""}{i.sku?` (${i.sku})`:""} · Saldo: {calcQty(i.id)} {i.unit}
                </option>
              ))}
            </Select>
          </Field>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label={movForm.type==="ajuste"?"Novo saldo (absoluto)":"Quantidade"}>
              <Input type="number" min="0" step="0.001" value={movForm.qty}
                onChange={e=>setMovForm(f=>({...f,qty:e.target.value}))}/>
            </Field>
            <Field label="Centro de Custo">
              <Select value={movForm.costCenter||allowedCostCenters[0]||(SL.COST_CENTERS||COST_CENTERS)[0]} onChange={e=>setMovForm(f=>({...f,costCenter:e.target.value}))}>
                {(allowedCostCenters.length?allowedCostCenters:(SL.COST_CENTERS||COST_CENTERS)).map(c=><option key={c}>{c}</option>)}
              </Select>
            </Field>
            {movForm.type==="transferencia"&&(
              <Field label="Centro de Custo Destino *">
                <Select value={movForm.destCostCenter||""} onChange={e=>setMovForm(f=>({...f,destCostCenter:e.target.value}))}>
                  <option value="">Selecione o destino...</option>
                  {(allowedCostCenters.length?allowedCostCenters:(SL.COST_CENTERS||COST_CENTERS)).filter(c=>c!==(movForm.costCenter||allowedCostCenters[0]||(SL.COST_CENTERS||COST_CENTERS)[0])).map(c=><option key={c}>{c}</option>)}
                </Select>
              </Field>
            )}
          </div>
          {movForm.itemId&&(()=>{
            const item=enrichedItems.find(i=>String(i.id)===String(movForm.itemId));
            if(!item) return null;
            const movType=STOCK_MOV_TYPES.find(t=>t.id===movForm.type);
            const afterQty=movForm.type==="ajuste"
              ?Number(movForm.qty||0)
              :item.quantity+(movType?.delta||0)*Number(movForm.qty||0);
            const c=afterQty<0?T.red:afterQty<=item.minQty?T.yellow:T.green;
            return(
              <div style={{background:`${c}12`,border:`1px solid ${c}33`,borderRadius:8,padding:"8px 14px",fontSize:12}}>
                <span style={{color:T.muted}}>Saldo atual: <strong style={{color:T.text}}>{item.quantity} {item.unit}</strong></span>
                {movForm.qty&&<span style={{marginLeft:16,color:T.muted}}>→ Após: <strong style={{color:c}}>{Number(afterQty).toFixed(3)} {item.unit}</strong></span>}
              </div>
            );
          })()}
          <Field label="Observações">
            <Input value={movForm.notes||""} placeholder="Motivo, N.F., referência..."
              onChange={e=>setMovForm(f=>({...f,notes:e.target.value}))}/>
          </Field>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setMovForm(null)}>Cancelar</Btn>
            <Btn onClick={saveMovement}>Registrar</Btn>
          </div>
        </div></div> : null;
  const renderInlineItemDetails = ()=> selectedItem ? <div style={inlinePanelStyle}>{selectedItem && <div style={{display:"grid",gap:14}}>
          <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"120px 1fr",gap:14,alignItems:"start"}}>
            <div>
              {(()=>{ const photos=getStockPhotos(selectedItem); const cover=photos[0]?.photo||photos[0]?.thumb||selectedItem.photo||""; return cover ? <img src={cover} alt="" onClick={()=>photos.length && setStockLightbox({photos:photos.map(x=>x.photo||x.thumb).filter(Boolean),idx:0})} style={{width:"100%",maxWidth:120,height:isCompactViewport()?180:120,objectFit:"cover",borderRadius:14,border:`1px solid ${T.border}`,cursor:photos.length?"zoom-in":"default"}}/> : <div style={{width:"100%",maxWidth:120,height:isCompactViewport()?180:120,borderRadius:14,border:`1px solid ${T.border}`,background:T.card2,display:"flex",alignItems:"center",justifyContent:"center",color:T.muted,fontSize:32}}>📦</div>; })()}
            </div>
            <div style={{display:"grid",gap:10}}>
              <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                {selectedItem.type && <Badge color={T.accent}>{selectedItem.type}</Badge>}
                {selectedItem.category && <Badge color={T.muted}>{selectedItem.category}</Badge>}
                {selectedItem.subcategory && <Badge color={T.green}>{selectedItem.subcategory}</Badge>}
                {selectedItem.costCenter && <CCBadge cc={selectedItem.costCenter}/>}
                {selectedItem.vetUse && <Badge color={T.purple}>💉 Vet.</Badge>}
                {selectedItem.controlled && <Badge color={T.red}>🔒 Controlado</Badge>}
                {selectedItem.forOrders && <Badge color={T.accent}>🛒 Pedidos</Badge>}
              </div>
              <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"repeat(2,minmax(0,1fr))",gap:10}}>
                <div style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:12}}><div style={{fontSize:11,color:T.muted,textTransform:"uppercase",fontWeight:700}}>Saldo atual</div><div style={{fontSize:20,fontWeight:800,color:Number(selectedItem.quantity||0)<=Number(selectedItem.minQty||0)?T.red:T.green}}>{selectedItem.quantity} {selectedItem.unit}</div></div>
                <div style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:12}}><div style={{fontSize:11,color:T.muted,textTransform:"uppercase",fontWeight:700}}>Último preço</div><div style={{fontSize:20,fontWeight:800,color:T.text}}>R$ {fmtMoney(selectedItem.unitCost||0)}</div></div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"repeat(2,minmax(0,1fr))",gap:10}}>
                <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:12}}><div style={{fontSize:11,color:T.muted,fontWeight:700,textTransform:"uppercase"}}>Estoque mínimo</div><div style={{fontSize:14,color:T.text,fontWeight:700}}>{selectedItem.minQty||0} {selectedItem.unit}</div></div>
                <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:12}}><div style={{fontSize:11,color:T.muted,fontWeight:700,textTransform:"uppercase"}}>Preço anterior</div><div style={{fontSize:14,color:T.text,fontWeight:700}}>{selectedItem.lastPrice!=null?`R$ ${fmtMoney(selectedItem.lastPrice)}`:'—'}</div></div>
                <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:12}}><div style={{fontSize:11,color:T.muted,fontWeight:700,textTransform:"uppercase"}}>Marca</div><div style={{fontSize:14,color:T.text,fontWeight:700}}>{selectedItem.brand||'—'}</div></div>
                <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:12}}><div style={{fontSize:11,color:T.muted,fontWeight:700,textTransform:"uppercase"}}>Fornecedor</div><div style={{fontSize:14,color:T.text,fontWeight:700}}>{selectedItem.supplier||'—'}</div></div>
                <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:12}}><div style={{fontSize:11,color:T.muted,fontWeight:700,textTransform:"uppercase"}}>Código / Ref.</div><div style={{fontSize:14,color:T.text,fontWeight:700}}>{selectedItem.sku||'—'}</div></div>
                <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:12}}><div style={{fontSize:11,color:T.muted,fontWeight:700,textTransform:"uppercase"}}>Código de barras</div><div style={{fontSize:14,color:T.text,fontWeight:700}}>{selectedItem.barcode||'—'}</div></div>
              </div>
              {!!selectedItem.notes && <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:12}}><div style={{fontSize:11,color:T.muted,fontWeight:700,textTransform:"uppercase",marginBottom:6}}>Observações</div><div style={{fontSize:13,color:T.text,whiteSpace:"pre-wrap"}}>{selectedItem.notes}</div></div>}
            </div>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
            <Btn variant="ghost" onClick={()=>setSelectedItem(null)}>Fechar</Btn>
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              {canImport && <Btn variant="ghost" onClick={()=>{ setMovForm({type:"entrada",qty:"",notes:"",itemId:selectedItem.id,costCenter:selectedItem.costCenter||allowedCostCenters[0]||COST_CENTERS[0]}); setSelectedItem(null); }}>➕ Entrada</Btn>}
              {canEdit && <Btn onClick={()=>{ const inferredType = selectedItem.type || (catalogCategoryOptions.find(entry=>String(entry.name||'')===String(selectedItem.category||''))?.typeName || ''); setForm({...selectedItem,_new:false,type:inferredType,subcategory:selectedItem.subcategory||'',pkgUnitRef:selectedItem.pkgUnitRef || getUnitConfig(selectedItem.unit)?.refUnit || ""}); setSelectedItem(null); }}>✏️ Editar</Btn>}
            </div>
          </div>
        </div>}</div> : null;
  const handleCatalogTypeChange = React.useCallback((typeName)=>{
    const categories = getCategoriesForType(typeName);
    const nextCategory = categories[0] || "";
    const subcategories = getSubcategoriesForCategory(nextCategory);
    setForm(f=>({ ...f, type:typeName, category:nextCategory, subcategory:subcategories[0] || "" }));
  }, [getCategoriesForType, getSubcategoriesForCategory]);
  const handleCatalogCategoryChange = React.useCallback((categoryName)=>{
    const subcategories = getSubcategoriesForCategory(categoryName);
    setForm(f=>({ ...f, category:categoryName, subcategory:subcategories[0] || "" }));
  }, [getSubcategoriesForCategory]);
  const isAnimalFeedType = String(form?.type||"")==="Alimento Animal";
  const isMedicationType = String(form?.type||"")==="Medicamento";
  const isLegacyFeedCategory = ["Ração","Suplementação","Feno"].includes(String(form?.category||""));
  const supportsAnimalProfiles = isAnimalFeedType || isMedicationType || isLegacyFeedCategory;
  const renderInlineCatalogForm = ()=> form ? <div style={inlinePanelStyle}><div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14}}>
          <div style={{gridColumn:"1/-1",display:"flex",gap:16,alignItems:"flex-start"}}>
            <div style={{display:"flex",flexDirection:"column",gap:10,flexShrink:0}}>
              <button type="button" onClick={()=>{const inp=document.createElement("input"); inp.type="file"; inp.accept="image/*"; inp.multiple=true; inp.onchange=async e=>{ for(const file of Array.from(e.target.files||[])){ const photo = await compressImage(file,1400,0.78); const thumb = await compressImage(file,320,0.64); setForm(f=>{ const photos=[...getStockPhotos(f), { id:createClientId("stkimg"), photo, thumb, title:"Foto do Produto", by:session?.name||"", ts:new Date().toISOString(), name:file.name||"" }]; return {...f, photos, photo:(photos[0]?.photo||f.photo||"")}; }); } }; inp.click(); }} style={{width:90,height:90,borderRadius:14,border:`2px dashed ${T.border}`,background:T.card2,cursor:"pointer",color:T.muted,display:"flex",alignItems:"center",justifyContent:"center",padding:0}}><ServicePhotoAddPhoneIcon size={24}/></button>
              <div style={{display:"inline-flex",alignItems:"center",gap:6,padding:"5px 8px",borderRadius:999,border:`1px solid ${stockHasPhotos(form)?`${T.accent}44`:T.border}`,background:stockHasPhotos(form)?`${T.accent}14`:T.card2,color:stockHasPhotos(form)?T.accent:T.muted,fontSize:11,fontWeight:700,alignSelf:"flex-start"}}><ServicePhotoStatusIcon size={16} hasPhotos={stockHasPhotos(form)}/><span>{stockHasPhotos(form)?getStockPhotos(form).length:0}</span></div>
            </div>
            <div style={{flex:1}}>
              <Field label="Nome do Produto *"><Input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Ex: Sal Proteico Bovino"/></Field>
              <Field label="Marca / Fabricante">
                <div style={{display:"grid",gridTemplateColumns:"1fr",gap:6}}>
                  <Select value={form.brand||""} onChange={e=>setForm(f=>({...f,brand:e.target.value}))}>
                    <option value="">Selecione a marca...</option>
                    {stockBrandOptions.map(b=><option key={b} value={b}>{b}</option>)}
                  </Select>
                  <div style={{fontSize:11,color:T.muted}}>Lista editável no painel do admin em <strong>Listas</strong> → <strong>Marcas de Produtos</strong>.</div>
                </div>
              </Field>
            </div>
          </div>
          <Field label="Fotos do Produto" col="1/-1">
            <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
              {getStockPhotos(form).map((ph,idx)=>(
                <div key={ph.id||idx} style={{display:"flex",flexDirection:"column",gap:4,alignItems:"center"}}>
                  <div style={{position:"relative"}}>
                    <img src={ph.thumb||ph.photo} alt="" onClick={()=>setStockLightbox({photos:getStockPhotos(form).map(x=>x.photo||x.thumb),idx})} style={{width:72,height:72,objectFit:"cover",borderRadius:10,cursor:"zoom-in",border:`1px solid ${T.border}`}}/>
                    <button onClick={()=>setForm(f=>{ const photos=getStockPhotos(f).filter((_,j)=>j!==idx); return {...f, photos, photo:(photos[0]?.photo||"")}; })} style={{position:"absolute",top:-6,right:-6,width:18,height:18,borderRadius:"50%",background:T.red,border:"none",color:"#fff",fontSize:11,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
                  </div>
                  <div style={{fontSize:10,color:T.muted,whiteSpace:"nowrap",maxWidth:72,overflow:"hidden",textOverflow:"ellipsis"}}>{ph.title||ph.name||"Foto"}</div>
                </div>
              ))}
              <button type="button" onClick={()=>{const inp=document.createElement("input"); inp.type="file"; inp.accept="image/*"; inp.multiple=true; inp.onchange=async e=>{ for(const file of Array.from(e.target.files||[])){ const photo = await compressImage(file,1400,0.78); const thumb = await compressImage(file,320,0.64); setForm(f=>{ const photos=[...getStockPhotos(f), { id:createClientId("stkimg"), photo, thumb, title:"Foto do Produto", by:session?.name||"", ts:new Date().toISOString(), name:file.name||"" }]; return {...f, photos, photo:(photos[0]?.photo||f.photo||"")}; }); } }; inp.click(); }} style={{width:72,height:72,borderRadius:10,border:`2px dashed ${T.border}`,background:T.card2,cursor:"pointer",color:T.muted,padding:0,display:"flex",alignItems:"center",justifyContent:"center"}}><ServicePhotoAddPhoneIcon size={20}/></button>
            </div>
          </Field>
          <Field label="Código / Ref."><Input value={form.sku||""} onChange={e=>setForm(f=>({...f,sku:e.target.value}))}/></Field>
          <Field label="Código de Barras"><Input value={form.barcode||""} onChange={e=>setForm(f=>({...f,barcode:e.target.value}))}/></Field>
          <Field label="Tipo">
            <div style={{display:"grid",gap:6}}>
              <Select value={form.type||""} onChange={e=>handleCatalogTypeChange(e.target.value)}>
                <option value="">Selecione o tipo...</option>
                {catalogTypeOptions.map(opt=><option key={opt} value={opt}>{opt}</option>)}
              </Select>
              <div style={{fontSize:11,color:T.muted}}>Lista editável no painel do admin em <strong>Listas</strong> → <strong>Tipos do Catálogo</strong>.</div>
            </div>
          </Field>
          <Field label="Categoria">
            <div style={{display:"grid",gap:6}}>
              <Select value={form.category||""} onChange={e=>handleCatalogCategoryChange(e.target.value)}>
                <option value="">Selecione a categoria...</option>
                {getCategoriesForType(form.type||"").map(opt=><option key={opt} value={opt}>{opt}</option>)}
              </Select>
              <div style={{fontSize:11,color:T.muted}}>As opções diminuem conforme o tipo escolhido.</div>
            </div>
          </Field>
          <Field label="Subcategoria">
            <div style={{display:"grid",gap:6}}>
              <Select value={form.subcategory||""} onChange={e=>setForm(f=>({...f,subcategory:e.target.value}))}>
                <option value="">Selecione a subcategoria...</option>
                {getSubcategoriesForCategory(form.category||"").map(opt=><option key={opt} value={opt}>{opt}</option>)}
              </Select>
              <div style={{fontSize:11,color:T.muted}}>Lista editável no painel do admin em <strong>Listas</strong> → <strong>Subcategorias do Catálogo</strong>.</div>
            </div>
          </Field>
          <Field label="Unidade">
            <Select value={form.unit||""} onChange={e=>applyUnitDefaults(e.target.value)}>
              {stockUnitOptions.map(u=><option key={u.label} value={u.label}>{u.label}</option>)}
            </Select>
          </Field>
          <Field label="Qtd Padrão por Embalagem"><Input type="number" step="0.001" value={form.pkgQty||""} onChange={e=>setForm(f=>({...f,pkgQty:e.target.value}))}/></Field>
          <Field label="Unidade de Referência"><Input value={form.pkgUnitRef||""} onChange={e=>setForm(f=>({...f,pkgUnitRef:e.target.value}))} placeholder="Ex.: L, kg, mL, un"/></Field>
          <Field label="Estoque Mínimo"><div style={{display:"grid",gap:6}}><Input type="number" step="0.001" value={form.minQty||0} onChange={e=>setForm(f=>({...f,minQty:Number(e.target.value)}))}/><div style={{fontSize:11,color:T.muted}}>Ao escolher a unidade, o app sugere a quantidade padrão e a unidade de referência cadastradas pelo administrador. Você pode ajustar esses valores só para este produto.</div></div></Field>
          <Field label="Preço de Custo (R$)"><Input type="number" step="0.01" value={form.unitCost||0} onChange={e=>setForm(f=>({...f,unitCost:Number(e.target.value)}))}/></Field>
          <Field label="Centro de Custo Padrão">
            <div style={{display:"grid",gridTemplateColumns:"1fr",gap:6}}>
              <Select value={form.costCenter||allowedCostCenters[0]||(SL.COST_CENTERS||COST_CENTERS)[0]} onChange={e=>setForm(f=>({...f,costCenter:e.target.value}))}>
                {(allowedCostCenters.length?allowedCostCenters:(SL.COST_CENTERS||COST_CENTERS)).map(c=><option key={c}>{c}</option>)}
              </Select>
              <div style={{fontSize:11,color:T.muted}}>Esse centro de custo virá preenchido por padrão ao utilizar o produto, mas poderá ser alterado no momento do uso.</div>
            </div>
          </Field>
          <Field label="Fornecedor Principal" col="1/-1">
            <Select value={form.supplierId||""} onChange={e=>{ const selected = supplierOptions.find(s=>String(s.id)===String(e.target.value)); setForm(f=>({...f,supplierId:e.target.value,supplier:selected?.name||""})); }}>
              <option value="">Selecione o fornecedor principal...</option>
              {supplierOptions.map(sup=><option key={sup.id} value={sup.id}>{sup.name}</option>)}
            </Select>
          </Field>
          {form._new&&<Field label="Saldo Inicial" col="1/-1">
            <Input type="number" min="0" step="0.001" value={form.initialQty||0} onChange={e=>setForm(f=>({...f,initialQty:e.target.value}))} placeholder="0 se produto novo"/>
          </Field>}
          <Field label="" col="1/-1">
            <div style={{display:"flex",flexWrap:"wrap",gap:10}}>
              {[
                {field:"vetUse",color:T.purple,icon:"💉",label:"Uso Veterinário",desc:"Aparece no módulo Vet"},
                {field:"forOrders",color:T.accent,icon:"🛒",label:"Disponível p/ Pedidos",desc:"Colaborador pode pedir"},
                {field:"controlled",color:T.red,icon:"🔒",label:"Controlado",desc:"Requer aprovação"},
              ].map(({field,color,icon,label,desc})=>(
                <label key={field} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:"8px 12px",borderRadius:8,
                  border:`1px solid ${form[field]?color:T.border}`,background:form[field]?`${color}15`:T.card2,flex:1,minWidth:160}}>
                  <input type="checkbox" checked={!!form[field]} onChange={e=>setForm(f=>({...f,[field]:e.target.checked}))} style={{width:16,height:16,accentColor:color}}/>
                  <div>
                    <div style={{fontWeight:600,color:form[field]?color:T.muted,fontSize:13}}>{icon} {label}</div>
                    <div style={{fontSize:10,color:T.muted}}>{desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </Field>
          {supportsAnimalProfiles && <Field label="Espécies atendidas" col="1/-1">
            <div style={{display:"grid",gap:10}}>
              <div style={{fontSize:12,color:T.muted}}>{isMedicationType ? "Marque as espécies para facilitar a filtragem dos produtos e configure abaixo as posologias por espécie/categoria." : "Produtos de Alimento Animal continuam com níveis de garantia e sugestões de consumo. Clique em uma espécie para exibir apenas as categorias dela."}</div>
              <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"repeat(4,minmax(0,1fr))",gap:10}}>
                {posologySpeciesOptions.map(species=>{
                  const checked=(form.targetSpecies||[]).includes(species);
                  return <label key={species} style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px",border:`1px solid ${checked?T.accent:T.border}`,borderRadius:12,background:checked?`${T.accent}12`:T.card2,cursor:"pointer"}}><input type="checkbox" checked={checked} onChange={e=>setForm(f=>{ const list=new Set(Array.isArray(f.targetSpecies)?f.targetSpecies:[]); if(e.target.checked) list.add(species); else list.delete(species); return {...f,targetSpecies:Array.from(list)}; })}/><button type="button" onClick={()=>checked && setSpeciesProfileFocus(species)} style={{border:"none",background:"transparent",padding:0,fontWeight:700,color:T.text,cursor:checked?"pointer":"default",textAlign:"left",flex:1}}>{species}</button></label>;
                })}
              </div>
              {!!(form.targetSpecies||[]).length && <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>{(form.targetSpecies||[]).map(species=>{ const active = speciesProfileFocus === species; return <button key={species} type="button" onClick={()=>setSpeciesProfileFocus(species)} style={{border:`1px solid ${active?T.green:T.border}`,background:active?`${T.green}15`:T.card2,color:active?T.green:T.text,borderRadius:999,padding:"8px 12px",fontWeight:700,cursor:"pointer"}}>{species}</button>; })}</div>}
              {!isMedicationType && <div style={{fontSize:12,color:T.muted}}>Você pode configurar consumos diferentes para o mesmo produto por espécie e também por categoria da espécie.</div>}
            </div>
          </Field>}
          {(supportsAnimalProfiles && !!speciesProfileFocus && !isMedicationType) && <Field label="Categorias da espécie selecionada" col="1/-1">
            <div style={{display:"grid",gap:10}}>
              <div style={{fontSize:12,color:T.muted}}>Somente as categorias de <strong style={{color:T.text}}>{speciesProfileFocus}</strong> ficam visíveis após o clique.</div>
              <div style={{border:`1px solid ${T.border}`,borderRadius:12,padding:12,display:"grid",gap:8,background:T.card2}}>
                <div style={{fontWeight:800,color:T.text}}>{speciesProfileFocus}</div>
                <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"repeat(2,minmax(0,1fr))",gap:8}}>{posologyCategoryOptions(speciesProfileFocus).map(category=>{ const checked=(form.nutritionProfiles||[]).some(profile=>String(profile?.species||"")===String(speciesProfileFocus) && String(profile?.category||"")===String(category)); const ageLabel = getAnimalCategoryAgeLabel(speciesProfileFocus, category); return <label key={category} style={{display:"grid",gap:4,padding:"8px 10px",border:`1px solid ${checked?T.green:T.border}`,borderRadius:10,background:checked?`${T.green}12`:T.card,cursor:"pointer"}}><div style={{display:"flex",alignItems:"center",gap:8}}><input type="checkbox" checked={checked} onChange={e=>setForm(f=>({...f,nutritionProfiles:ensureProfileByCategory(f.nutritionProfiles, "nutrition", speciesProfileFocus, category, e.target.checked)}))}/><span style={{color:T.text,fontWeight:600}}>{category}</span></div>{ageLabel && <span style={{fontSize:11,color:T.muted,marginLeft:26}}>{ageLabel}</span>}</label>; })}</div>
              </div>
            </div>
          </Field>}
          {((isAnimalFeedType || ["Ração","Suplementação"].includes(String(form.category||""))))&&<Field label="Níveis de Garantia" col="1/-1"><div style={{display:"grid",gap:10}}><div style={{fontSize:12,color:T.muted}}>Cadastre cada nível de garantia separadamente. Ex.: Proteína 100 g/kg.</div>{(form.guaranteeItems||[]).map((entry,idx)=><div key={entry.id||idx} style={{border:`1px solid ${T.border}`,borderRadius:12,padding:12,display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1.4fr 1fr 1fr auto",gap:10,background:T.card2}}><Field label="Item"><Input value={entry.nutrient||""} placeholder="Proteína, Cálcio, Energia..." onChange={e=>setForm(f=>{ const list=[...(f.guaranteeItems||[])]; list[idx]={...list[idx],nutrient:e.target.value}; return {...f,guaranteeItems:list,guaranteeLevels:formatGuaranteeItems(list)}; })}/></Field><Field label="Quantidade"><Input type="number" min="0" step="0.0001" value={entry.amount||""} onChange={e=>setForm(f=>{ const list=[...(f.guaranteeItems||[])]; list[idx]={...list[idx],amount:e.target.value}; return {...f,guaranteeItems:list,guaranteeLevels:formatGuaranteeItems(list)}; })}/></Field><Field label="Medida"><Select value={entry.unit||"g/kg"} onChange={e=>setForm(f=>{ const list=[...(f.guaranteeItems||[])]; list[idx]={...list[idx],unit:e.target.value}; return {...f,guaranteeItems:list,guaranteeLevels:formatGuaranteeItems(list)}; })}>{guaranteeUnitOptions.map(opt=><option key={opt} value={opt}>{opt}</option>)}</Select></Field><div style={{display:"flex",alignItems:"flex-end"}}><Btn variant="danger" onClick={()=>setForm(f=>{ const list=(f.guaranteeItems||[]).filter((_,i)=>i!==idx); return {...f,guaranteeItems:list,guaranteeLevels:formatGuaranteeItems(list)}; })}>Remover</Btn></div></div>)}<div style={{display:"flex",gap:10,flexWrap:"wrap"}}><Btn variant="ghost" onClick={()=>setForm(f=>{ const list=[...(f.guaranteeItems||[]), createGuaranteeDraft()]; return {...f,guaranteeItems:list}; })}>+ Adicionar nível</Btn>{!!(form.guaranteeItems||[]).length && <div style={{fontSize:12,color:T.muted,alignSelf:"center"}}>{formatGuaranteeItems(form.guaranteeItems)}</div>}</div></div></Field>}
          {supportsAnimalProfiles && <Field label={isMedicationType?"Área de Posologia":"Sugestões de Consumo"} col="1/-1">
            <div style={{display:"grid",gap:10}}>
              <div style={{fontSize:12,color:T.muted}}>{isMedicationType ? "Cadastre perfis por espécie, categoria e sexo. Em medicamento, peso + espécie + categoria + sexo ajudam no cálculo e também na validação de elegibilidade do uso." : "Cadastre perfis por espécie e categoria para o app sugerir a quantidade mais comum por animal, por 100 animais ou por peso vivo."}</div>
              {((isMedicationType ? form.posologyProfiles : form.nutritionProfiles) || []).map((profile, idx)=><div key={profile.id||idx} style={{border:`1px solid ${T.border}`,borderRadius:12,padding:12,display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"repeat(3,minmax(0,1fr))",gap:10,background:T.card2}}>
                <Field label="Espécie"><Select value={profile.species||"Ave"} onChange={e=>setForm(f=>{ const field=isMedicationType?"posologyProfiles":"nutritionProfiles"; const list=[...(f[field]||[])]; const species=e.target.value; list[idx]={...list[idx], species, category:(posologyCategoryOptions(species)[0]||"Geral")}; return {...f,[field]:list}; })}>{posologySpeciesOptions.map(opt=><option key={opt} value={opt}>{opt}</option>)}</Select></Field>
                <Field label="Categoria do animal"><div style={{display:"grid",gap:6}}><Select value={profile.category||""} onChange={e=>setForm(f=>{ const field=isMedicationType?"posologyProfiles":"nutritionProfiles"; const list=[...(f[field]||[])]; list[idx]={...list[idx], category:e.target.value}; return {...f,[field]:list}; })}>{posologyCategoryOptions(profile.species||"Ave").map(opt=><option key={opt} value={opt}>{opt}</option>)}</Select>{getAnimalCategoryAgeLabel(profile.species||"Ave", profile.category||"") && <div style={{fontSize:11,color:T.muted}}>{getAnimalCategoryAgeLabel(profile.species||"Ave", profile.category||"")}</div>}</div></Field>
                <Field label={isMedicationType?"Sexo habilitado":"Base do cálculo"}>{isMedicationType ? <Select value={profile.sex||"Ambos"} onChange={e=>setForm(f=>{ const list=[...(f.posologyProfiles||[])]; list[idx]={...list[idx], sex:e.target.value}; return {...f,posologyProfiles:list}; })}><option value="Ambos">Ambos</option><option value="Macho">Macho</option><option value="Fêmea">Fêmea</option></Select> : <Select value={profile.basis||"perAnimal"} onChange={e=>setForm(f=>{ const list=[...(f.nutritionProfiles||[])]; list[idx]={...list[idx], basis:e.target.value}; return {...f,nutritionProfiles:list}; })}><option value="perAnimal">Por animal</option><option value="per100Animals">A cada 100 animais</option><option value="perKgLiveweight">Por kg de peso vivo</option></Select>}</Field>
                {isMedicationType ? <Field label="Modo de posologia"><Select value={profile.dosageMode||"mlPerKg"} onChange={e=>setForm(f=>{ const list=[...(f.posologyProfiles||[])]; const mode=e.target.value; list[idx]={...list[idx], dosageMode:mode, basis:mode==="singleDose"?"perAnimal":"perKgLiveweight", doseUnit:mode==="singleDose"?(list[idx]?.doseUnit||"dose"):(list[idx]?.doseUnit||"mL")}; return {...f,posologyProfiles:list}; })}><option value="mlPerKg">mL/kg</option><option value="singleDose">Dose única</option></Select></Field> : null}
                <Field label={isMedicationType?"Quantidade sugerida":"Consumo sugerido"}><Input type="number" min="0" step="0.0001" value={profile.dose||""} onChange={e=>setForm(f=>{ const field=isMedicationType?"posologyProfiles":"nutritionProfiles"; const list=[...(f[field]||[])]; list[idx]={...list[idx], dose:e.target.value}; return {...f,[field]:list}; })}/></Field>
                <Field label={isMedicationType?"Unidade de aplicação":"Unidade de medida"}><Input value={profile.doseUnit||""} placeholder={isMedicationType?"mL, dose, comprimido":"kg, g, saco"} onChange={e=>setForm(f=>{ const field=isMedicationType?"posologyProfiles":"nutritionProfiles"; const list=[...(f[field]||[])]; list[idx]={...list[idx], doseUnit:e.target.value}; return {...f,[field]:list}; })}/></Field>
                <Field label={isMedicationType?"Peso referência (kg, opcional)":"Peso referência (kg, opcional)"}><Input type="number" min="0" step="0.1" value={profile.referenceWeightKg||""} onChange={e=>setForm(f=>{ const field=isMedicationType?"posologyProfiles":"nutritionProfiles"; const list=[...(f[field]||[])]; list[idx]={...list[idx], referenceWeightKg:e.target.value}; return {...f,[field]:list}; })}/></Field>
                {isMedicationType && <Field label="Uso habilitado"><label style={{display:"flex",alignItems:"center",gap:8,height:42}}><input type="checkbox" checked={profile.allowed !== false} onChange={e=>setForm(f=>{ const list=[...(f.posologyProfiles||[])]; list[idx]={...list[idx], allowed:e.target.checked}; return {...f,posologyProfiles:list}; })}/> Pode usar neste perfil</label></Field>}
                <Field label="Observações" col="1/-1"><Textarea rows={2} value={profile.notes||""} onChange={e=>setForm(f=>{ const field=isMedicationType?"posologyProfiles":"nutritionProfiles"; const list=[...(f[field]||[])]; list[idx]={...list[idx], notes:e.target.value}; return {...f,[field]:list}; })}/></Field>
                <div style={{gridColumn:"1/-1",display:"flex",justifyContent:"flex-end"}}><Btn variant="danger" onClick={()=>setForm(f=>{ const field=isMedicationType?"posologyProfiles":"nutritionProfiles"; return {...f,[field]:(f[field]||[]).filter((_,i)=>i!==idx)}; })}>Remover perfil</Btn></div>
              </div>)}
              <div><Btn variant="ghost" onClick={()=>setForm(f=>{ const field=isMedicationType?"posologyProfiles":"nutritionProfiles"; const species = speciesProfileFocus || (f.targetSpecies||[])[0] || "Ave"; return {...f,[field]:[...(f[field]||[]), createProfileDraft(field==="nutritionProfiles"?"nutrition":"medication", species)]}; })}>+ Adicionar perfil</Btn></div>
            </div>
          </Field>}
          {String(form.category||"").toLowerCase()==='feno' && <>
            <Field label="Peso do Fardo (kg)"><Input type="number" step="0.01" value={form.baleWeightKg||form.pkgQty||''} onChange={e=>setForm(f=>({...f,baleWeightKg:e.target.value,pkgQty:e.target.value||f.pkgQty,pkgUnitRef:'kg'}))} /></Field>
            <Field label="Forrageira"><Input value={form.forageBase||''} onChange={e=>setForm(f=>({...f,forageBase:e.target.value}))} placeholder="Ex.: Tifton 85, Coast-cross, Jiggs..." /></Field>
            <Field label="Qualidade do Feno"><Select value={form.hayQuality||'Boa'} onChange={e=>setForm(f=>({...f,hayQuality:e.target.value}))}>{['Muito boa','Boa','Regular','Ruim'].map(opt=><option key={opt}>{opt}</option>)}</Select></Field>
            <Field label="Níveis de Garantia do Feno" col="1/-1"><Textarea rows={4} value={form.hayGuaranteeLevels||form.guaranteeLevels||''} placeholder="PB, FDN, FDA, Ca, P..." onChange={e=>setForm(f=>({...f,hayGuaranteeLevels:e.target.value,guaranteeLevels:e.target.value}))}/></Field>
          </>}
          <Field label="Observações" col="1/-1"><Textarea rows={2} value={form.notes||""} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/></Field>
          <div style={{gridColumn:"1/-1",display:"flex",gap:10,justifyContent:"space-between"}}>
            <div>{!form._new&&canEdit&&<Btn variant="danger" onClick={()=>{applyNormalizedItems((items||[]).filter(i=>i.id!==form.id));setForm(null);show("🗑️ Produto removido.");syncOnSave("stock");}}>🗑️ Excluir</Btn>}</div>
            <div style={{display:"flex",gap:10}}>
              <Btn variant="ghost" onClick={()=>setForm(null)}>Cancelar</Btn>
              {canEdit&&<Btn onClick={saveItem}>Salvar</Btn>}
            </div>
          </div>
        </div></div> : null;
  const renderInlineInvoiceEditor = ()=> editInvoice ? <div style={inlinePanelStyle}><div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="Número"><Input value={editInvoice.numero} onChange={e=>setEditInvoice(f=>({...f,numero:e.target.value}))}/></Field>
            <Field label="Série"><Input value={editInvoice.serie} onChange={e=>setEditInvoice(f=>({...f,serie:e.target.value}))}/></Field>
            <Field label="Data de Emissão"><Input type="date" value={editInvoice.emissao} onChange={e=>setEditInvoice(f=>({...f,emissao:e.target.value}))}/></Field>
            <Field label="Valor Total (R$)"><Input type="number" step="0.01" value={editInvoice.totalNF} onChange={e=>setEditInvoice(f=>({...f,totalNF:Number(e.target.value)}))}/></Field>
            <Field label="Fornecedor" col="1/-1"><Input value={editInvoice.fornecedor} onChange={e=>setEditInvoice(f=>({...f,fornecedor:e.target.value}))}/></Field>
            <Field label="CNPJ"><Input value={editInvoice.cnpj} onChange={e=>setEditInvoice(f=>({...f,cnpj:e.target.value}))}/></Field>
            <Field label="Natureza da Operação"><Input value={editInvoice.natOp} onChange={e=>setEditInvoice(f=>({...f,natOp:e.target.value}))}/></Field>
          </div>
          <div style={{borderTop:`1px solid ${T.border}`,paddingTop:14}}>
            <div style={{fontSize:12,fontWeight:700,color:T.muted,marginBottom:10}}>PRODUTOS</div>
            {editInvoice.prods.map((p,i)=>(
              <div key={i} style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",gap:8,marginBottom:8,alignItems:"center"}}>
                <Input value={p.name} onChange={e=>setEditInvoice(f=>({...f,prods:f.prods.map((pp,ii)=>ii===i?{...pp,name:e.target.value}:pp)}))} style={{fontSize:12}}/>
                <Input type="number" value={p.qty} onChange={e=>setEditInvoice(f=>({...f,prods:f.prods.map((pp,ii)=>ii===i?{...pp,qty:Number(e.target.value)}:pp)}))} style={{fontSize:12}}/>
                <Input type="number" step="0.01" value={p.unitVal} onChange={e=>setEditInvoice(f=>({...f,prods:f.prods.map((pp,ii)=>ii===i?{...pp,unitVal:Number(e.target.value),total:Number(e.target.value)*pp.qty}:pp)}))} style={{fontSize:12}}/>
                <span style={{fontWeight:600,color:T.green,fontSize:12}}>R$ {fmtMoney(p.qty*p.unitVal)}</span>
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setEditInvoice(null)}>Cancelar</Btn>
            <Btn onClick={()=>{setInvoices(p=>p.map(i=>i.id===editInvoice.id?editInvoice:i));setEditInvoice(null);show("✅ Nota atualizada!");}}>Salvar</Btn>
          </div>
        </div></div> : null;
  const renderInlineOrderLink = ()=> movOrderLink ? <div style={{...inlinePanelStyle,padding:isCompactViewport()?14:16}}>          <div style={{fontSize:13,color:T.muted,marginBottom:16}}>
            Esta movimentação foi gerada pelo Pedido <strong>#{movOrderLink}</strong>.
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setMovOrderLink(null)}>Fechar</Btn>
            {onNavigate&&<Btn onClick={()=>{setMovOrderLink(null);onNavigate("purchases",{openOrderId:movOrderLink});}}>🛒 Ver Pedido</Btn>}
          </div></div> : null;
  const renderInlineFilterPanel = ()=> stockFilterOpen ? <div style={{...inlinePanelStyle,padding:isCompactViewport()?14:16}}>        <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"repeat(2,minmax(0,1fr))",gap:12}}>
          <Field label="Categoria"><Select value={stockFilters.category} onChange={e=>setStockFilters(f=>({...f,category:e.target.value}))}><option value="">Todas</option>{stockFilterOptions.categories.map(opt=><option key={opt} value={opt}>{opt}</option>)}</Select></Field>
          <Field label="Marca"><Select value={stockFilters.brand} onChange={e=>setStockFilters(f=>({...f,brand:e.target.value}))}><option value="">Todas</option>{stockFilterOptions.brands.map(opt=><option key={opt} value={opt}>{opt}</option>)}</Select></Field>
          <Field label="Fornecedor Principal"><Select value={stockFilters.supplier} onChange={e=>setStockFilters(f=>({...f,supplier:e.target.value}))}><option value="">Todos</option>{stockFilterOptions.suppliers.map(opt=><option key={opt} value={opt}>{opt}</option>)}</Select></Field>
          <Field label="Centro de Custo Padrão"><Select value={stockFilters.costCenter} onChange={e=>setStockFilters(f=>({...f,costCenter:e.target.value}))}><option value="">Todos</option>{stockFilterOptions.costCenters.map(opt=><option key={opt} value={opt}>{opt}</option>)}</Select></Field>
          <Field label="Unidade"><Select value={stockFilters.unit} onChange={e=>setStockFilters(f=>({...f,unit:e.target.value}))}><option value="">Todas</option>{stockFilterOptions.units.map(opt=><option key={opt} value={opt}>{opt}</option>)}</Select></Field>
          <Field label="Situação do Estoque"><Select value={stockFilters.stockState} onChange={e=>setStockFilters(f=>({...f,stockState:e.target.value}))}><option value="">Todas</option><option value="low">Estoque baixo</option><option value="withBalance">Com saldo</option><option value="noBalance">Sem saldo</option></Select></Field>
          <div style={{gridColumn:"1/-1",display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap"}}>
            <div style={{fontSize:12,color:T.muted}}>{activeStockFiltersCount} filtro(s) ativo(s) • {filteredStockItems.length} item(ns) encontrados</div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              <Btn variant="ghost" onClick={()=>setStockFilters({category:"",brand:"",supplier:"",costCenter:"",unit:"",stockState:""})}>Limpar</Btn>
              <Btn onClick={()=>setStockFilterOpen(false)}>Aplicar</Btn>
            </div>
          </div>
        </div></div> : null;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div>
      <Toast toast={toast}/>
      <input ref={xmlInputRef} type="file" accept=".xml,text/xml,application/xml" onChange={handleXmlFile} style={{display:"none"}}/>

      {/* Tabs */}
      {!hideTabs && <ModuleHeaderMenu
        T={T}
        title="Menu de Estoque"
        items={[
          ...(session?.role === "admin" && showCatalogTab !== false ? [{ id:"opcoes", label:"Catálogo", icon:"⚙️", active:tab==="opcoes", onClick:()=>setTab("opcoes") }] : []),
          { id:"stock", label:"Estoque", icon:"📦", active:tab==="stock", onClick:()=>setTab("stock") },
          ...(canImport ? [{ id:"import", label:"Importar NF-e", icon:"📥", active:tab==="import", onClick:()=>setTab("import") }] : []),
          { id:"movements", label:"Movimentações", icon:"📋", active:tab==="movements", onClick:()=>setTab("movements") },
          { id:"invoices", label:"Notas Fiscais", icon:"🧾", active:tab==="invoices", onClick:()=>setTab("invoices") },
        ]}
      />}

      {tab!=="stock" && renderInlineMovementForm()}

      {/* ── TAB: STOCK ─────────────────────────────────────────────────────── */}
      {tab==="stock"&&<>
        {lowStock.length>0&&<div style={{background:`${T.red}18`,border:`1px solid ${T.red}`,borderRadius:10,padding:"10px 16px",marginBottom:16,fontSize:13,color:T.red}}>⚠️ Estoque baixo: {lowStock.map(i=>i.name).join(", ")}</div>}
        {stockCardSummaries.length>0 && <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:12,marginBottom:16}}>{stockCardSummaries.map(card=><div key={card.id} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14,display:"grid",gap:6,position:"relative",overflow:"hidden"}}>{card.icon && <div style={{position:"absolute",top:12,right:12,width:52,height:52,minWidth:52,borderRadius:14,background:T.card2,border:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 10px 18px rgba(15,23,42,.08)"}}>{card.icon}</div>}<div style={{fontSize:11,color:T.muted,textTransform:"uppercase",fontWeight:700,paddingRight:card.icon?64:0}}>{card.label}</div><div style={{fontSize:22,fontWeight:800,color:T.text,lineHeight:1,paddingRight:card.icon?64:0}}>{card.value}</div><div style={{fontSize:12,color:T.green,paddingRight:card.icon?64:0}}>{card.subLabel||card.category||""}</div><div style={{fontSize:11,color:T.muted,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{card.helper}</div></div>)}</div>}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,gap:8,flexWrap:"wrap"}}>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <Btn variant={stockSearchOpen || stockSearchText ? "primary" : "ghost"} onClick={()=>{ if(stockSearchOpen && !stockSearchText){ setStockSearchOpen(false); return; } setStockSearchOpen(v=>!v); }}>🔎 Pesquisar</Btn>
            <Btn variant={stockFilterOpen || activeStockFiltersCount ? "primary" : "ghost"} onClick={()=>setStockFilterOpen(true)}>🧰 Filtrar{activeStockFiltersCount?` (${activeStockFiltersCount})`:""}</Btn>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {canMove&&<Btn variant="ghost" onClick={()=>setMovForm({type:"saida",qty:"",notes:"",itemId:"",costCenter:allowedCostCenters[0] || COST_CENTERS[0]})}>➖ Registrar Saída</Btn>}
            {canImport&&<Btn variant="ghost" onClick={()=>setMovForm({type:"entrada",qty:"",notes:"",itemId:"",costCenter:allowedCostCenters[0] || COST_CENTERS[0]})}>➕ Entrada Manual</Btn>}
          </div>
        </div>
        {renderInlineMovementForm()}
        {(stockSearchOpen || stockSearchText) && <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:12,marginBottom:12}}>
          <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"minmax(240px,1fr) auto auto",gap:8,alignItems:"center"}}>
            <Input value={stockSearchText} onChange={e=>setStockSearchText(e.target.value)} placeholder="Buscar por número, nome, marca, fornecedor, categoria ou unidade" />
            {!!stockSearchText && <Btn variant="ghost" onClick={()=>setStockSearchText("")}>Limpar busca</Btn>}
            <div style={{fontSize:12,color:T.muted,textAlign:isCompactViewport()?"left":"right"}}>{filteredStockItems.length} item(ns) encontrado(s)</div>
          </div>
        </div>}
        {renderInlineFilterPanel()}
        {renderInlineItemDetails()}
        {(activeStockFiltersCount>0 || stockFilterSummary.length>0) && <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginBottom:12}}>
          <span style={{fontSize:12,color:T.muted,fontWeight:700}}>Filtros ativos:</span>
          {stockFilterSummary.map(label=><span key={label} style={{display:"inline-flex",alignItems:"center",padding:"6px 10px",borderRadius:999,border:`1px solid ${T.border}`,background:T.card2,fontSize:12,color:T.text}}>{label}</span>)}
          <Btn variant="ghost" onClick={()=>setStockFilters({category:"",brand:"",supplier:"",costCenter:"",unit:"",stockState:""})}>Limpar filtros</Btn>
        </div>}
        <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,overflow:"hidden"}}>
          <div style={{display:"grid",gridTemplateColumns:"56px minmax(0,1.7fr) minmax(0,0.9fr) minmax(0,1fr)",gap:8,alignItems:"center",padding:"10px 12px",background:T.card2,borderBottom:`1px solid ${T.border}`}}>
            {['Fotos','Item','Qtd','Últ. Preço'].map(h=><div key={h} style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:.6}}>{h}</div>)}
          </div>
          {filteredStockItems.map((r, idx)=>{
            const photos = getStockPhotos(r);
            const thumb = photos[0]?.thumb || photos[0]?.photo || r.photo || "";
            const low = Number(r.quantity||0) <= Number(r.minQty||0);
            return <button key={r.id || idx} type="button" onClick={()=>setSelectedItem(r)} style={{width:"100%",display:"grid",gridTemplateColumns:"56px minmax(0,1.7fr) minmax(0,0.9fr) minmax(0,1fr)",gap:8,alignItems:"center",padding:"10px 12px",border:"none",borderBottom:idx<filteredStockItems.length-1?`1px solid ${T.border}22`:"none",background:"transparent",textAlign:"left",cursor:"pointer"}}>
              <div style={{display:"flex",justifyContent:"center"}}>
                {thumb ? <img src={thumb} alt="" style={{width:40,height:40,objectFit:"cover",borderRadius:10,border:`1px solid ${T.border}`}}/> : <div style={{width:40,height:40,borderRadius:10,border:`1px solid ${T.border}`,background:T.card2,display:"flex",alignItems:"center",justifyContent:"center",color:T.muted}}><ServicePhotoStatusIcon size={16} hasPhotos={false}/></div>}
              </div>
              <div style={{minWidth:0}}>
                <div style={{fontWeight:700,color:T.text,fontSize:13,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.name}</div>
                <div style={{fontSize:11,color:T.muted,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{[r.type, r.category, r.subcategory].filter(Boolean).join(' • ') || 'Sem classificação'}{r.brand?` • ${r.brand}`:''}</div>
              </div>
              <div style={{minWidth:0,fontSize:12,fontWeight:700,color:low?T.red:T.green,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.quantity} {r.unit}</div>
              <div style={{minWidth:0,fontSize:12,fontWeight:700,color:T.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>R$ {fmtMoney(r.unitCost||0)}</div>
            </button>;
          })}
        </div>
        {filteredStockItems.length===0&&<div style={{color:T.muted,textAlign:"center",padding:"26px 14px",background:T.card,border:`1px solid ${T.border}`,borderRadius:14,marginTop:12}}>Nenhum item encontrado com a busca/filtros aplicados.</div>}
      </>}

      {/* ── TAB: OPÇÕES ────────────────────────────────────────────────────── */}
      {tab==="opcoes"&&canEdit&&<>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div>
            <div style={{fontWeight:700,color:T.text,fontSize:16}}>📋 Catálogo de Produtos</div>
            <div style={{fontSize:12,color:T.muted,marginTop:2}}>Gerencie o catálogo. Os produtos aqui aparecem nos Pedidos e na baixa de Estoque.</div>
          </div>
          {canCreate&&<Btn onClick={()=>setForm({...defForm,_new:true})}>+ Novo Produto</Btn>}
        </div>
        {renderInlineCatalogForm()}

        {!form && <>
        {/* Search */}
        <Input value={""} placeholder="🔍 Buscar produto..." style={{marginBottom:14}}
          onChange={()=>{}}/>

        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:12}}>
          {items.map(item=>{
            const qty = calcQty(item.id);
            const low = qty <= (item.minQty||0);
            return(
              <div key={item.id} style={{background:T.card,border:`1px solid ${low?T.red:T.border}`,borderRadius:14,overflow:"hidden",transition:"all .2s"}}
                onMouseEnter={e=>e.currentTarget.style.borderColor=T.accent}
                onMouseLeave={e=>e.currentTarget.style.borderColor=low?T.red:T.border}>
                {/* Photo + header */}
                <div style={{height:80,background:item.photo?`url(${item.photo}) center/cover`:T.card2,
                  display:"flex",alignItems:"flex-end",position:"relative"}}>
                  {!item.photo&&<div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",opacity:.2,fontSize:36}}>📦</div>}
                  <div style={{padding:"6px 12px",background:"linear-gradient(transparent,rgba(0,0,0,.7))",width:"100%"}}>
                    <div style={{fontSize:10,fontWeight:800,color:"rgba(255,255,255,.82)",letterSpacing:.6,marginBottom:2}}>{displayStockRecordNumber(item)}</div>
                    <div style={{fontWeight:700,color:"#fff",fontSize:14,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{item.name}</div>
                    {item.brand&&<div style={{fontSize:11,color:"rgba(255,255,255,.7)"}}>{item.brand}</div>}
                  </div>
                </div>
                <div style={{padding:"10px 14px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                    <Badge color={T.muted}>{item.category}</Badge>
                    <span style={{fontWeight:700,fontSize:14,color:low?T.red:T.green}}>{qty} {item.unit}</span>
                  </div>
                  {item.sku&&<div style={{fontSize:11,color:T.muted}}>Ref: {item.sku}</div>}
                  {item.supplier&&<div style={{fontSize:11,color:T.muted}}>Fornecedor: {item.supplier}</div>}
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:6}}><span style={{fontSize:11,color:T.muted}}>Fotos do produto</span><span style={{display:"inline-flex",alignItems:"center",gap:6,padding:"4px 8px",borderRadius:999,border:`1px solid ${stockHasPhotos(item)?`${T.accent}44`:T.border}`,background:stockHasPhotos(item)?`${T.accent}14`:T.card2,color:stockHasPhotos(item)?T.accent:T.muted,fontSize:11,fontWeight:700}}><ServicePhotoStatusIcon size={16} hasPhotos={stockHasPhotos(item)}/><span>{stockHasPhotos(item)?getStockPhotos(item).length:0}</span></span></div>
                  <div style={{display:"flex",gap:4,marginTop:6,flexWrap:"wrap"}}>
                    {item.vetUse&&<Badge color={T.purple}>💉 Vet</Badge>}
                    {item.forOrders&&<Badge color={T.accent}>🛒 Pedidos</Badge>}
                    {item.controlled&&<Badge color={T.red}>🔒 Controlado</Badge>}
                  </div>
                  <div style={{display:"flex",gap:6,marginTop:10}}>
                    <Btn size="sm" variant="ghost" style={{flex:1}} onClick={()=>setForm({...item,_new:false,pkgUnitRef:item.pkgUnitRef || getUnitConfig(item.unit)?.refUnit || ""})}>✏️ Editar</Btn>
                    <Btn size="sm" variant="ghost" style={{flex:1}} onClick={()=>setMovForm({type:"entrada",qty:"",notes:"",itemId:item.id,costCenter:item.costCenter||allowedCostCenters[0]||COST_CENTERS[0]})}>➕ Entrada</Btn>
                  </div>
                </div>
              </div>
            );
          })}
          {items.length===0&&<div style={{gridColumn:"1/-1",color:T.muted,textAlign:"center",padding:48}}>
            Nenhum produto cadastrado. Clique em '+ Novo Produto' para começar.
          </div>}
        </div>
        </>}
      </>}

      {/* ── TAB: MOVEMENTS ─────────────────────────────────────────────────── */}
      {tab==="movements"&&<>
        {renderInlineOrderLink()}
        <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,overflow:"hidden"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1.5fr 90px 90px 1fr 1fr 32px",padding:"10px 16px",background:T.card2,borderBottom:`1px solid ${T.border}`}}>
            {["Data","Item","Tipo","Qtd","Por","Obs.",""].map(h=>(
              <div key={h} style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:.8}}>{h}</div>
            ))}
          </div>
          {[...movements].sort((a,b)=>b.ts?.localeCompare(a.ts||"")||0).map((m,midx)=>{
            const item=items.find(i=>String(i.id)===String(m.itemId));
            const movType=STOCK_MOV_TYPES.find(t=>t.id===m.type)||{icon:"📦",color:T.muted,label:m.type};
            // Extract order ID from notes if it's a purchase movement
            const orderMatch=m.notes?.match(/Pedido #(\d+)/);
            const orderNum=orderMatch?orderMatch[1]:null;
            return(
              <div key={m.id} style={{display:"grid",gridTemplateColumns:"1fr 1.5fr 90px 90px 1fr 1fr 32px",
                padding:"10px 16px",borderBottom:midx<movements.length-1?`1px solid ${T.border}22`:"none",
                background:T.card,transition:"background .15s"}}>
                <div style={{fontSize:12,color:T.muted}}>{m.ts?new Date(m.ts).toLocaleString("pt-BR",{day:"2-digit",month:"2-digit",year:"2-digit",hour:"2-digit",minute:"2-digit"}):"—"}</div>
                <div style={{fontSize:12,fontWeight:600,color:T.text}}>{item?.name||"—"}</div>
                <div><Badge color={movType.color}>{movType.icon} {movType.label}</Badge></div>
                <div style={{fontSize:13,fontWeight:700,color:movType.color}}>{m.type==="saida"||m.type==="descarte"?"-":m.type==="ajuste"?"=":"+"}{m.qty} {item?.unit||""}</div>
                <div style={{fontSize:12,color:T.muted}}>{m.by}</div>
                <div style={{fontSize:11,color:T.muted,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{m.notes||"—"}</div>
                <div style={{display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {orderNum&&<button
                    title={`Ver Pedido #${orderNum}`}
                    onClick={()=>setMovOrderLink(orderNum)}
                    style={{background:"none",border:"none",color:T.accent,cursor:"pointer",fontSize:14,padding:4}}>🔗</button>}
                </div>
              </div>
            );
          })}
          {movements.length===0&&<div style={{color:T.muted,textAlign:"center",padding:40}}>Nenhuma movimentação registrada ainda.</div>}
        </div>
        {/* Order link modal */}
        
      </>}

      {/* ── TAB: INVOICES ──────────────────────────────────────────────────── */}
      {tab==="invoices"&&<>
        {renderInlineInvoiceEditor()}
        {invoices.length===0&&<div style={{color:T.muted,padding:40,textAlign:"center"}}>Nenhuma NF-e importada ainda.</div>}
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          {[...invoices].sort((a,b)=>b.id-a.id).map(inv=>(
            <div key={inv.id} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,overflow:"hidden"}}>
              {/* Header */}
              <div style={{background:`${T.purple}18`,borderBottom:`1px solid ${T.purple}33`,padding:"14px 18px",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
                <div>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
                    <span style={{fontSize:18}}>🧾</span>
                    <span style={{fontFamily:"'Playfair Display',serif",fontSize:16,fontWeight:700,color:T.text}}>NF-e {inv.numero}-{inv.serie}</span>
                    <Badge color={T.purple}>Importada em {fmtDate(inv.importedAt)}</Badge>
                  </div>
                  <div style={{fontSize:12,color:T.muted,display:"flex",gap:14,flexWrap:"wrap"}}>
                    <span>📅 Emissão: {fmtDate(inv.emissao)}</span>
                    <span>🏭 {inv.fornecedor}</span>
                    {inv.cnpj&&<span style={{fontFamily:"monospace"}}>CNPJ: {inv.cnpj}</span>}
                    {inv.natOp&&<span>📋 {inv.natOp}</span>}
                  </div>
                </div>
                <div className="fz-app-header-left" style={{display:"flex",alignItems:"center",gap:12}}>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:11,color:T.muted}}>Valor Total</div>
                    <div style={{fontWeight:700,fontSize:18,color:T.green}}>R$ {fmtMoney(inv.totalNF)}</div>
                  </div>
                  {canEdit&&<Btn size="sm" variant="ghost" onClick={()=>setEditInvoice({...inv})}>✏️ Editar</Btn>}
                  {canEdit&&<Btn size="sm" variant="danger" onClick={()=>setInvoices(p=>p.filter(i=>i.id!==inv.id))}>🗑️</Btn>}
                </div>
              </div>
              {/* Products */}
              <div style={{padding:"12px 18px"}}>
                <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>{inv.prods.length} produto(s)</div>
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead><tr style={{borderBottom:`1px solid ${T.border}`}}>
                      {["Produto","Qtd","Un.","Preço Unit.","Total","Ação"].map(h=><th key={h} style={{padding:"6px 10px",textAlign:"left",color:T.muted,fontWeight:700,textTransform:"uppercase",fontSize:10,letterSpacing:.6}}>{h}</th>)}
                    </tr></thead>
                    <tbody>{inv.prods.map((p,i)=>(
                      <tr key={i} style={{borderBottom:`1px solid ${T.border}22`}}>
                        <td style={{padding:"8px 10px",color:T.text,fontWeight:500}}>{p.name}</td>
                        <td style={{padding:"8px 10px",color:T.text}}>{p.qty}</td>
                        <td style={{padding:"8px 10px",color:T.muted}}>{p.unit}</td>
                        <td style={{padding:"8px 10px",color:T.text}}>R$ {fmtMoney(p.unitVal)}</td>
                        <td style={{padding:"8px 10px",fontWeight:600,color:T.green}}>R$ {fmtMoney(p.total)}</td>
                        <td style={{padding:"8px 10px"}}><Badge color={p.action==="create"?T.accent:T.green}>{p.action==="create"?"Criado":"Atualizado"}</Badge></td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
            </div>
          ))}
        </div>
      </>}

      {/* ── TAB: IMPORT NF-e ───────────────────────────────────────────────── */}
      {tab==="import"&&canImport&&<>
        {!nfeData&&!nfePreview&&<>
          <div style={{background:T.card,border:`2px dashed ${T.border}`,borderRadius:16,padding:48,textAlign:"center"}}>
            <div style={{fontSize:48,marginBottom:16}}>🧾</div>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:700,color:T.text,marginBottom:8}}>Importar NF-e XML</div>
            <div style={{color:T.muted,fontSize:14,marginBottom:24,maxWidth:420,margin:"0 auto 24px"}}>
              Selecione o arquivo XML da Nota Fiscal Eletrônica. O sistema irá ler os produtos, quantidades e preços automaticamente.
            </div>
            <Btn onClick={()=>xmlInputRef.current.click()} size="lg">
              {nfeLoading?"Processando...":"📂 Selecionar arquivo XML"}
            </Btn>
            <div style={{marginTop:16,fontSize:12,color:T.muted}}>Suporta NF-e padrão SEFAZ (versão 3.x e 4.x)</div>
          </div>
        </>}

        {nfeData&&nfePreview&&<>
          {/* NF-e summary */}
          <div style={{background:`${T.purple}18`,border:`1px solid ${T.purple}44`,borderRadius:12,padding:"16px 20px",marginBottom:20}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12}}>
              <div>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:18,fontWeight:700,color:T.text,marginBottom:6}}>🧾 NF-e {nfeData.numero} — Série {nfeData.serie}</div>
                <div style={{display:"flex",gap:16,flexWrap:"wrap",fontSize:13,color:T.muted}}>
                  <span>📅 Emissão: <strong style={{color:T.text}}>{fmtDate(nfeData.emissao)}</strong></span>
                  <span>🏭 <strong style={{color:T.text}}>{nfeData.nomeEmit||"—"}</strong></span>
                  {nfeData.cnpjEmit&&<span style={{fontFamily:"monospace"}}>CNPJ: {nfeData.cnpjEmit}</span>}
                  {nfeData.natOp&&<span>📋 {nfeData.natOp}</span>}
                </div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:11,color:T.muted}}>Valor Total da Nota</div>
                <div style={{fontWeight:700,fontSize:22,color:T.green}}>R$ {fmtMoney(nfeData.totalNF)}</div>
              </div>
            </div>
          </div>

          {/* Product mapping table */}
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,overflow:"hidden",marginBottom:20}}>
            <div style={{padding:"14px 18px",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontFamily:"'Playfair Display',serif",fontSize:16,fontWeight:700,color:T.text}}>{nfePreview.length} produto(s) encontrado(s)</span>
              <div style={{display:"flex",gap:6}}>
                <Badge color={T.green}>{nfePreview.filter(p=>p.action==="update").length} atualizar</Badge>
                <Badge color={T.accent}>{nfePreview.filter(p=>p.action==="create").length} criar</Badge>
                <Badge color={T.muted}>{nfePreview.filter(p=>p.action==="skip").length} ignorar</Badge>
              </div>
            </div>
            <div style={{overflowX:"auto"}}>
              {nfePreview.map(p=>(
                <div key={p.idx} style={{padding:"14px 18px",borderBottom:`1px solid ${T.border}22`,background:p.action==="skip"?`${T.muted}08`:p.action==="update"?`${T.green}06`:`${T.accent}06`}}>
                  <div style={{display:"flex",alignItems:"flex-start",gap:14,flexWrap:"wrap"}}>
                    {/* Product info */}
                    <div style={{flex:"0 0 220px",minWidth:160}}>
                      <div style={{fontWeight:600,color:p.action==="skip"?T.muted:T.text,fontSize:14}}>{p.name}</div>
                      <div style={{fontSize:11,color:T.muted,marginTop:3}}>
                        {p.qty} {p.unit} · R$ {fmtMoney(p.unitVal)}/un · <strong style={{color:T.green}}>R$ {fmtMoney(p.total)}</strong>
                      </div>
                      {p.ncm&&<div style={{fontSize:10,color:T.muted,marginTop:1}}>NCM: {p.ncm}</div>}
                    </div>
                    {/* Previous price if existing */}
                    {p.action==="update"&&<div style={{flex:"0 0 160px",background:T.card2,borderRadius:8,padding:"8px 12px",fontSize:12}}>
                      <div style={{color:T.muted,fontSize:10,marginBottom:3}}>ESTOQUE ATUAL</div>
                      <div style={{color:T.text,fontWeight:600}}>{p.existingName}</div>
                      <div style={{color:T.muted}}>Qtd: {p.existingQty} · Preço ant.: R$ {fmtMoney(p.lastPrice)}</div>
                    </div>}
                    {/* Controls */}
                    <div style={{flex:1,display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                      {/* Category (only for create) */}
                      {p.action==="create"&&<div style={{minWidth:140}}>
                        <div style={{fontSize:10,color:T.muted,marginBottom:3}}>CATEGORIA</div>
                        <select value={p.category} onChange={e=>updatePreviewRow(p.idx,"category",e.target.value)} style={{...inputStyle,fontSize:12,padding:"5px 8px"}}>
                          {catalogTypeOptions.map(c=><option key={c}>{c}</option>)}
                        </select>
                      </div>}
                      {p.action==="create"&&<div style={{minWidth:140}}>
                        <div style={{fontSize:10,color:T.muted,marginBottom:3}}>CENTRO</div>
                        <select value={p.costCenter} onChange={e=>updatePreviewRow(p.idx,"costCenter",e.target.value)} style={{...inputStyle,fontSize:12,padding:"5px 8px"}}>
                          {(allowedCostCenters.length?allowedCostCenters:(SL.COST_CENTERS||COST_CENTERS)).map(c=><option key={c}>{c}</option>)}
                        </select>
                      </div>}
                      <div style={{display:"flex",alignItems:"center",gap:6,alignSelf:"flex-end",paddingBottom:2}}>
                        <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",padding:"5px 10px",background:p.vetUse?`${T.purple}22`:`${T.muted}11`,border:`1px solid ${p.vetUse?T.purple:T.border}`,borderRadius:8,whiteSpace:"nowrap"}}>
                          <input type="checkbox" checked={!!p.vetUse} onChange={e=>updatePreviewRow(p.idx,"vetUse",e.target.checked)} style={{accentColor:T.purple}}/>
                          <span style={{fontSize:11,fontWeight:600,color:p.vetUse?T.purple:T.muted}}>💉 Uso Vet.</span>
                        </label>
                      </div>
                      {/* Action buttons */}
                      <div style={{display:"flex",gap:6,marginLeft:"auto"}}>
                        {["update","create"].includes(p.action)&&p.existingId&&<button onClick={()=>updatePreviewRow(p.idx,"action","update")} style={{padding:"5px 12px",borderRadius:8,fontSize:11,fontWeight:600,cursor:"pointer",border:`1.5px solid ${p.action==="update"?T.green:T.border}`,background:p.action==="update"?`${T.green}22`:"transparent",color:p.action==="update"?T.green:T.muted}}>➕ Atualizar estoque</button>}
                        <button onClick={()=>updatePreviewRow(p.idx,"action","create")} style={{padding:"5px 12px",borderRadius:8,fontSize:11,fontWeight:600,cursor:"pointer",border:`1.5px solid ${p.action==="create"?T.accent:T.border}`,background:p.action==="create"?`${T.accent}22`:"transparent",color:p.action==="create"?T.accent:T.muted}}>✨ Criar novo</button>
                        <button onClick={()=>updatePreviewRow(p.idx,"action","skip")} style={{padding:"5px 12px",borderRadius:8,fontSize:11,fontWeight:600,cursor:"pointer",border:`1.5px solid ${p.action==="skip"?T.muted:T.border}`,background:p.action==="skip"?`${T.muted}22`:"transparent",color:p.action==="skip"?T.muted:T.muted}}>⏭ Ignorar</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Action buttons */}
          <div style={{display:"flex",gap:12,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>{setNfeData(null);setNfePreview(null);}}>← Cancelar</Btn>
            <Btn onClick={confirmImport} disabled={nfePreview.every(p=>p.action==="skip")}>
              ✅ Confirmar Importação ({nfePreview.filter(p=>p.action!=="skip").length} produtos)
            </Btn>
          </div>
        </>}
      </>}

      {/* Edit invoice modal (admin only) */}
      
      {/* ─── MODALS (outside tabs so they work from any tab) ─── */}

      {/* Movement Modal */}
      

      {/* Product Details Modal */}
      
      {/* Product Catalog Modal */}
      
      
      {stockLightbox&&<div onClick={()=>setStockLightbox(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.92)",zIndex:1400,display:"flex",alignItems:"center",justifyContent:"center",padding:16,cursor:"zoom-out"}}><img src={stockLightbox.photos[stockLightbox.idx]} alt="" style={{maxWidth:"92vw",maxHeight:"84vh",objectFit:"contain",borderRadius:12,boxShadow:"0 18px 40px rgba(0,0,0,.45)",cursor:"auto"}} onClick={e=>e.stopPropagation()}/></div>}
    </div>
  );
}


}

export default createStockModule;
